"use client";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  Panel,
  MarkerType,
  MiniMap,
} from "reactflow";
import throttle from "lodash/throttle";
import "reactflow/dist/style.css";
import "./KnowledgeGraph.css";
import {
  forceSimulation,
  forceLink,
  forceManyBody,
  forceCollide,
  forceCenter,
} from "d3-force";
import dagre from "dagre";
import {
  loadKnowledgeGraph,
  // uploadMultiplePdfsAndGetGraph,
  uploadMaterials,
  extractDraft,
  discardDraft,
  listGraphs,
  listMaterials,
  getProgressInsights,
  createSyllabusDraft,
  commitSyllabusDraft,
  createConceptDraft,
  commitConceptDraft,
  fetchCanonicalConcepts,
  saveKnowledgeGraphSnapshot,
} from "../../services/topicsService";
import NodeEditModal from "./NodeEditModal";
import { API_URL } from "../../config";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";

const VIEW_MODES = { MERGED: "merged", PER_LECTURE: "perLecture" };
const ENABLE_STUDENT_PERFORMANCE = false;

const readAuthToken = () => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem("token");
  } catch (err) {
    console.warn("Unable to read auth token from localStorage", err);
    return null;
  }
};

export const getAuthHeaders = () => {
  const token = readAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fetchWithAuth = (url, options = {}) => {
  const headers = { ...(options.headers || {}), ...getAuthHeaders() };
  return fetch(url, { ...options, headers });
};

async function postJSON(url, body) {
  const r = await fetchWithAuth(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error((await r.text()) || `HTTP ${r.status}`);
  return r.json();
}

export const api = {
  createSyllabusDraft: (material_id, graph_name, course_id) =>
    postJSON("/api/knowledge-graph/syllabus/draft", { material_id, graph_name, course_id }),
  commitSyllabusDraft: (draft_id, concepts, graph_name) =>
    postJSON("/api/knowledge-graph/syllabus/commit", { draft_id, concepts, graph_name }),
  extractDraft: (material_ids, graph_name) =>
    postJSON("/api/knowledge-graph/extract-draft", { material_ids, graph_name }),
  saveChanges: (nodes, edges, graph_id) =>
    postJSON("/api/knowledge-graph/save-changes", { nodes, edges, graph_id }),
  loadFromNeo4j: () => fetchWithAuth("/api/knowledge-graph/load-from-neo4j").then(r => r.json()),
};


function KnowledgeGraph() {
  const navigate = useNavigate();
  const location = useLocation();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);
  // Unified node editor handles both create and edit
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [edgeToEdit, setEdgeToEdit] = useState(null);
  const [edgeLabelDraft, setEdgeLabelDraft] = useState("");
  const [baseNodes, setBaseNodes] = useState([]);
  const [baseEdges, setBaseEdges] = useState([]);
  const [studentNodes, setStudentNodes] = useState([]);
  const [studentEdges, setStudentEdges] = useState([]);
  const [showStudents, setShowStudents] = useState(false);

  // Add the missing state variables that your friend added
  const [profile, setProfile] = useState(null);
  const [selectedNetId, setSelectedNetId] = useState(null);
  const [assessmentErrors, setAssessmentErrors] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [studentScopedView, setStudentScopedView] = useState(false);

  // full in-memory graph
  const [allNodes, setAllNodes] = useState([]);
  const [allEdges, setAllEdges] = useState([]);

  // Add this new state for multi-file uploads
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadDescription, setUploadDescription] = useState("");
  const [labels, setLabels] = useState([]);
  const [rels, setRels] = useState([]);
  const [relsLoading, setRelsLoading] = useState(false);
  const [relsError, setRelsError] = useState("");
  const [showAllRelationsActive, setShowAllRelationsActive] = useState(false);
  const [relDirection, setRelDirection] = useState("any"); // 'out' | 'in' | 'any'

  const [draftBatchId, setDraftBatchId] = useState(null); // also used as current graph id when viewing a draft/published graph
  const [showNodeEdit, setShowNodeEdit] = useState(false);
  const [currentGraphId, setCurrentGraphId] = useState(null); // null = main graph
  const [graphs, setGraphs] = useState([]);
  const [graphListReloadTick, setGraphListReloadTick] = useState(0);
  const [materials, setMaterials] = useState([]);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState([]);
  const [canonicalMap, setCanonicalMap] = useState({});
  const [canonicalLoading, setCanonicalLoading] = useState(false);
  const [canonicalError, setCanonicalError] = useState("");

  // Add ref to track if we've loaded initially
  const hasLoadedInitially = useRef(false);

  // Dynamic assessment options
  const [assessmentOptions, setAssessmentOptions] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState("");
  const [assessmentsLoading, setAssessmentsLoading] = useState(true);
  const [progressSummary, setProgressSummary] = useState(null);
  const [progressStatus, setProgressStatus] = useState(null);

  // Panel visibility controls
  const [leftPanelVisible, setLeftPanelVisible] = useState(true);
  const [rightPanelVisible, setRightPanelVisible] = useState(true);

  const PAGE = 12; // how many to show per batch
  const [labelLimit, setLabelLimit] = useState(PAGE);
  const [relLimit, setRelLimit] = useState(PAGE);

    // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [syllabusDraft, setSyllabusDraft] = useState(null);
  const [syllabusDraftConcepts, setSyllabusDraftConcepts] = useState([]);
  const [draftError, setDraftError] = useState("");
  const [isDraftSubmitting, setIsDraftSubmitting] = useState(false);
  const [conceptDraft, setConceptDraft] = useState(null);
  const [conceptDraftConcepts, setConceptDraftConcepts] = useState([]);
  const [selectedConceptAnchors, setSelectedConceptAnchors] = useState([]);
  const [isConceptSubmitting, setIsConceptSubmitting] = useState(false);
  const [graphSelectedAnchors, setGraphSelectedAnchors] = useState([]);
  const [graphName, setGraphName] = useState("");
  const [isSnapshotSaving, setIsSnapshotSaving] = useState(false);
  const [snapshotMessage, setSnapshotMessage] = useState("");

  const [materialId, setMaterialId] = useState("");   // the uploaded file's id
  const [draftId, setDraftId] = useState(null);

  // 1) Create syllabus draft → fills concepts for review
async function handleCreateSyllabusDraft() {
  const res = await api.createSyllabusDraft(materialId, graphName || undefined);
  setDraftId(res.draft_id);
  // backend returns objects; keep just labels for simple editing UI
  setSyllabusDraftConcepts((res.concepts || []).map(c => c.label).filter(Boolean));
}

// 2) Commit syllabus draft (send edited labels as strings)
async function handleCommitSyllabus() {
  if (!draftId) return alert("No draft");
  const res = await api.commitSyllabusDraft(draftId, syllabusDraftConcepts, graphName || undefined);
  console.log("Committed:", res);
  // optionally reload graph so anchors show up
  const loaded = await api.loadFromNeo4j();
  setNodes(loaded?.data?.nodes || []);
  setEdges(loaded?.data?.edges || []);
}

// (optional) Extract a draft from multiple uploads you already have
async function handleExtractDraft(materialIdsArray) {
  const res = await api.extractDraft(materialIdsArray, graphName || undefined);
  setNodes(res.nodes || []);
  setEdges(res.edges || []);
  // save positions later:
  // await api.saveChanges(res.nodes, res.edges, res.batch_id);
}

  const mergeAnchors = useCallback((base = [], additions = []) => {
    const seen = new Set();
    const merged = [];
    base.forEach((label) => {
      const trimmed = (label || "").trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(trimmed);
    });
    additions.forEach((label) => {
      const trimmed = (label || "").trim();
      if (!trimmed) return;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(trimmed);
    });
    return merged;
  }, []);

  const anchorsEqual = useCallback((a = [], b = []) => {
    if (a === b) return true;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }, []);

  const splitGraph = useCallback((input, maybeEdges) => {
    const graphNodes = Array.isArray(input) ? input : input?.nodes || [];
    const graphEdges = Array.isArray(input) ? maybeEdges || [] : input?.edges || [];

    const studentNodes = [];
    const documentNodes = [];
    const baseEdges = []; // we’ll ignore edges for the root-only view

    for (const node of graphNodes) {
      const labels = node?.labels || [];
      const type = node?.data?.nodeType || "";
      const props = node?.data?.properties || {};
      const label = node?.data?.label || "";

      // ✅ Top-level Students (only direct Student nodes)
      const isTopStudent =
        (labels.includes("Student") ||
          type === "Student" ||
          type.toLowerCase() === "student" ||
          props?.netid ||
          /@/.test(label)) &&
        !labels.includes("StudentAssessment") &&
        !labels.includes("StudentQuestion") &&
        !labels.includes("StudentError");

      // ✅ Top-level Documents
      const isTopDocument =
        labels.includes("Document") ||
        type === "Document" ||
        type.toLowerCase() === "document";

      if (isTopStudent) studentNodes.push(node);
      else if (isTopDocument) documentNodes.push(node);
    }

    // Combine both categories for your root-level display
    let rootNodes = [...studentNodes, ...documentNodes];
    let rootEdges = [];

    // Fallback: if no students/documents, show the full graph (e.g., Concept-only subgraphs)
    if (rootNodes.length === 0) {
      rootNodes = graphNodes;
      rootEdges = graphEdges;
    }

    return {
      baseNodes: rootNodes,
      baseEdges: rootEdges, // keep edges when we use the full graph fallback
      studentNodes,
      studentEdges: [],
    };
  }, []);


  const canonicalMetadata = useMemo(() => {
    if (!selectedNode?.data?.properties) return null;
    const props = selectedNode.data.properties;
    const metaJson = props.canonical_metadata_json || props.canonical_metadata;
    if (typeof metaJson === "string") {
      try {
        return JSON.parse(metaJson);
      } catch (err) {
        console.warn("Failed to parse canonical metadata JSON", err);
        return null;
      }
    }
    if (metaJson && typeof metaJson === "object") {
      return metaJson;
    }
    return null;
  }, [selectedNode]);

  const conceptAnchorChoices = useMemo(() => {
    const seen = new Set();
    const choices = [];
    const candidates = (allNodes && allNodes.length ? allNodes : nodes) || [];
    candidates.forEach((node) => {
      const data = node?.data || {};
      const props =
        data.properties ||
        node?.properties ||
        node?.props ||
        {};

      const type = (data.nodeType || node?.nodeType || "").toLowerCase();
      const labels = (data.labels || node?.labels || []).map((lbl) =>
        typeof lbl === "string" ? lbl.toLowerCase() : ""
      );

      if (
        type.startsWith("student") ||
        props.student_netid ||
        props.netid ||
        labels.some((lbl) => lbl.startsWith("student"))
      ) {
        return;
      }

      const preferred =
        data.label ||
        data.name ||
        props.canonical_label ||
        props.name ||
        props.label ||
        props.display_name ||
        node?.label ||
        node?.id;
      const cleaned = (preferred || "").trim();
      if (!cleaned) return;

      const key = cleaned.toLowerCase();
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      choices.push({ value: cleaned, label: cleaned });
    });
    return choices;
  }, [allNodes, nodes]);

  const conceptAnchorSet = useMemo(() => {
    const set = new Set();
    conceptAnchorChoices.forEach((choice) => {
      if (!choice?.value) return;
      set.add(choice.value.toLowerCase());
    });
    return set;
  }, [conceptAnchorChoices]);

  const normalizeLabel = useCallback((label) => {
    if (!label || typeof label !== "string") return "";
    return label.trim().toLowerCase();
  }, []);

  const mergeCanonicalConcepts = useCallback(
    (items = []) => {
      setCanonicalMap((prev) => {
        const map = { ...prev };
        items.forEach((concept = {}) => {
          const label = concept.canonical_label || concept.label;
          const key = normalizeLabel(label);
          if (!key) return;
          const current = map[key] || {};
          map[key] = {
            ...current,
            canonical_label: concept.canonical_label || label,
            status: concept.status ?? current.status ?? "needs_review",
            wikidata_qid: concept.wikidata_qid ?? current.wikidata_qid ?? null,
            source: concept.source ?? current.source ?? null,
            metadata: concept.metadata ?? current.metadata ?? null,
          };
          const aliases = concept.metadata?.aliases || concept.metadata?.original_labels;
          if (Array.isArray(aliases)) {
            aliases.forEach((alias) => {
              const aliasKey = normalizeLabel(alias);
              if (!aliasKey) return;
              const existing = map[aliasKey] || {};
              map[aliasKey] = {
                ...existing,
                canonical_label: concept.canonical_label || label,
                status: concept.status ?? existing.status ?? "needs_review",
                wikidata_qid: concept.wikidata_qid ?? existing.wikidata_qid ?? null,
                source: concept.source ?? existing.source ?? null,
                metadata: concept.metadata ?? existing.metadata ?? null,
              };
            });
          }
        });
        return map;
      });
    },
    [normalizeLabel]
  );

  const loadCanonicalConcepts = useCallback(async () => {
    setCanonicalLoading(true);
    setCanonicalError("");
    try {
      const payload = await fetchCanonicalConcepts();
      mergeCanonicalConcepts(payload.concepts || []);
    } catch (err) {
      console.error("Failed to fetch canonical concepts", err);
      setCanonicalError(err.message || "Unable to load canonical concepts");
    } finally {
      setCanonicalLoading(false);
    }
  }, [mergeCanonicalConcepts]);

  const getStatusTheme = useCallback((status) => {
    const normalized = (status || "").toLowerCase();
    if (normalized === "verified") {
      return {
        border: "#86efac",
        background: "#ecfdf5",
        text: "#166534",
        label: "verified",
      };
    }
    if (normalized === "needs_review") {
      return {
        border: "#f97316",
        background: "#fff7ed",
        text: "#b45309",
        label: "needs review",
      };
    }
    return {
      border: "#cbd5f5",
      background: "#eef2ff",
      text: "#312e81",
      label: normalized || "pending",
    };
  }, []);

  const resolveLabelTheme = useCallback(
    (label) => {
      const canonical = canonicalMap[normalizeLabel(label)] || null;
      if (canonicalLoading && !canonical) {
        return {
          canonical: null,
          theme: {
            border: "#bfdbfe",
            background: "#eff6ff",
            text: "#1d4ed8",
            label: "loading…",
          },
        };
      }
      return {
        canonical,
        theme: getStatusTheme(canonical?.status),
      };
    },
    [canonicalMap, canonicalLoading, normalizeLabel, getStatusTheme]
  );

  useEffect(() => {
    if (!selectedConceptAnchors.length) return;
    const available = new Set(conceptAnchorChoices.map((opt) => opt.value));
    const filtered = selectedConceptAnchors.filter((label) => available.has(label));
    if (filtered.length !== selectedConceptAnchors.length) {
      setSelectedConceptAnchors(filtered);
    }
  }, [conceptAnchorChoices, selectedConceptAnchors]);

  useEffect(() => {
    if (!selectedTypes.includes("concepts") && selectedConceptAnchors.length) {
      setSelectedConceptAnchors([]);
    }
  }, [selectedTypes, selectedConceptAnchors]);

  useEffect(() => {
    if (!conceptDraft) return;
    if (!graphSelectedAnchors.length) return;
    const validAnchors = graphSelectedAnchors.filter((label) =>
      conceptAnchorSet.has((label || "").trim().toLowerCase())
    );
    if (!validAnchors.length) return;
    setSelectedConceptAnchors((prev) => {
      const merged = mergeAnchors(prev, validAnchors);
      return anchorsEqual(prev, merged) ? prev : merged;
    });
  }, [conceptDraft?.draft_id, graphSelectedAnchors, conceptAnchorSet, mergeAnchors, anchorsEqual]);

  // ---- Physics layout (Neo4j-like) ----
const simRef = useRef(null);

  const resetUploadState = useCallback(() => {
    setUploadFiles([]);
    setSelectedTypes([]);
    setSyllabusDraft(null);
    setSyllabusDraftConcepts([]);
    setDraftError("");
    setIsDraftSubmitting(false);
    setConceptDraft(null);
    setConceptDraftConcepts([]);
    setSelectedConceptAnchors([]);
    setIsConceptSubmitting(false);
    setIsUploading(false);
    setGraphSelectedAnchors([]);
    setGraphName("");
  }, []);

  const startForce = useCallback(
    (ns, es) => {
      if (!ns.length) return;
      if (simRef.current) simRef.current.stop();

      const simNodes = ns.map((n) => ({
        id: n.id,
        x: n.position?.x ?? 0,
        y: n.position?.y ?? 0,
        vx: 0,
        vy: 0,
      }));
      const simLinks = es.map((e) => ({ source: e.source, target: e.target }));

      const sim = forceSimulation(simNodes)
        .force(
          "link",
          forceLink(simLinks)
            .id((d) => d.id)
            .distance(140)
            .strength(0.12)
        )
        .force("charge", forceManyBody().strength(-220)) // repel
        .force("collide", forceCollide(45)) // no overlap
        .force("center", forceCenter(0, 0))
        .alpha(0.7)
        .alphaDecay(0.08)
        .on("tick", () => {
          setNodes((prev) =>
            prev.map((n) => {
              const s = simNodes.find((x) => x.id === n.id);
              return s ? { ...n, position: { x: s.x, y: s.y } } : n;
            })
          );
        });

      simRef.current = sim;
    },
    [setNodes]
  );

// (re)start physics whenever the # of nodes/edges changes
useEffect(() => {
  startForce(nodes, edges);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [nodes.length, edges.length]);

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 180;
const nodeHeight = 60;

function applyLayout(nodes, edges, direction = "LR") {
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((n) => {
    dagreGraph.setNode(n.id, { width: nodeWidth, height: nodeHeight });
  });
  edges.forEach((e) => {
    dagreGraph.setEdge(e.source, e.target);
  });

  dagre.layout(dagreGraph);

  return nodes.map((n) => {
    const pos = dagreGraph.node(n.id);
    return { ...n, position: { x: pos.x, y: pos.y } };
  });
}

const refreshProgressInsights = useCallback(
  async (assessmentId) => {
    if (!assessmentId) {
      setProgressSummary(null);
      setProgressStatus(null);
      return;
    }
    try {
      const res = await getProgressInsights(assessmentId);
      if (res.status === "success") {
        setProgressSummary(res.data);
        setProgressStatus("cached");
      } else {
        setProgressStatus(res.status);
        if (res.status !== "not_found") {
          console.warn("Progress insights status:", res.status);
        }
      }
    } catch (error) {
      console.error("🔥 FRONTEND: Failed to load cached progress insights:", error);
      setProgressStatus("error");
    }
  },
  []
);

useEffect(() => {
  if (showUploadModal) {
    loadCanonicalConcepts();
  }
}, [showUploadModal, loadCanonicalConcepts]);

useEffect(() => {
  console.log("🔥 TOGGLE:", { showStudents });
  console.log("Base nodes:", baseNodes.length, "Student nodes:", studentNodes.length);
  console.log("Base edges:", baseEdges.length, "Student edges:", studentEdges.length);

  const combinedNodes = showStudents
    ? [...baseNodes, ...studentNodes]
    : baseNodes;
  const combinedEdges = showStudents
    ? [...baseEdges, ...studentEdges]
    : baseEdges;

  console.log("🔥 Combined nodes to render:", combinedNodes.length);

  setNodes(combinedNodes);
  setEdges(combinedEdges);

  if (reactFlowInstance && combinedNodes.length > 0) {
    reactFlowInstance.fitView({ padding: 0.1 });
  }
}, [showStudents, baseNodes, studentNodes, baseEdges, studentEdges, reactFlowInstance]);



  //   const mapApiNode = (n) => ({
  //   id: n.id,
  //   data: {
  //     label: n.display ||
  //            n.props?.name ||
  //            n.props?.title ||
  //            n.props?.display_name ||
  //            n.props?.label ||
  //            n.props?.id ||
  //            (n.labels?.[0] || "Node"),
  //     properties: n.props || {},
  //     nodeType: n.labels?.[0] || "Unknown",
  //   },
  //   position: { x: 200, y: 200 },
  // });

  // Stable edge IDs so duplicates from the API don't collide in React
  const mapApiEdge = (e) => {
    const type = e.type || e.label || "REL";
    const safeId = e.id || `e:${e.source}:${type}:${e.target}`;
    return {
      id: safeId,
      source: e.source,
      target: e.target,
      label: e.label || e.type,
      data: { type: e.type, ...e.props },
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { strokeWidth: 2 },
    };
  };
// Keep only the first item for each key (used to dedupe nodes/edges)
const uniqBy = (arr = [], keyFn) => {
  const m = new Map();
  for (const x of arr || []) {
    const k = keyFn(x);
    if (!m.has(k)) m.set(k, x);
  }
  return [...m.values()];
};


const GRID_COLS = 4,
  GRID_X = 180,
  GRID_Y = 140,
  PAD_X = 40,
  PAD_Y = 40;

const mapApiNode = (n, i = 0) => {
  const properties = n.properties || n.props || {};

  let label =
    n.labels?.includes("Student")
      ? properties?.netid || "Student"
      : n.display ||
        properties?.display_label ||
        properties?.name ||
        properties?.title ||
        properties?.display_name ||
        properties?.label ||
        properties?.id ||
        (n.labels?.[0] || "Node");

  // 🔹 SQL Error simplification
  if (n.labels?.includes("StudentError") && typeof label === "string") {
    const match = label.match(/SQL Error:\s*\d+\s*\(\d+\)/);
    if (match) label = match[0];
  }

  const savedX = properties?.saved_x ?? properties?.position_x;
  const savedY = properties?.saved_y ?? properties?.position_y;
  const defaultX = PAD_X + (i % GRID_COLS) * GRID_X + Math.random() * 20;
  const defaultY = PAD_Y + Math.floor(i / GRID_COLS) * GRID_Y + Math.random() * 20;

  const hasReviewFlag = Boolean(
    properties?.canonical_needs_review ||
      (Array.isArray(properties?.needs_review_concepts) &&
        properties.needs_review_concepts.length > 0)
  );
  const canonicalStatus = properties?.canonical_status;

  const nodeType =
    (n.labels?.includes("Syllabus Topics") && "Syllabus Topics") ||
    (n.labels?.includes("Lecture") && "Lecture") ||
    (n.labels?.includes("Concept") && "Concept") ||
    n.labels?.[0] ||
    "Unknown";

  let nodeStyle = {
    borderRadius: 10,
    border: "1.5px solid #ccc",
    background: "#fff",
    fontSize: 13,
    fontWeight: 500,
    width: 160,
    height: 60,
    textAlign: "center",
  };

  if (hasReviewFlag) {
    nodeStyle = { ...nodeStyle, border: "2px solid #f97316", background: "#fff7ed" };
  } else if (canonicalStatus === "verified") {
    nodeStyle = { ...nodeStyle, border: "2px solid #22c55e", background: "#f0fdf4" };
  }

  // 🔹 Minimal change: swap colors
  if (nodeType === "Syllabus Topics") {
    nodeStyle = {
      ...nodeStyle,
      background: "#E8F5E9", // light green
      border: "2px solid #388E3C",
      fontWeight: 700,
      fontSize: 15,
      width: 240,
      height: 100,
    };
  } else if (nodeType === "Lecture") {
    nodeStyle = {
      ...nodeStyle,
      background: "#FFFFFF",
      border: "2px solid #555",
      width: 140,
      height: 60,
      fontWeight: 600,
    };
  } else if (nodeType === "Concept") {
    nodeStyle = {
      ...nodeStyle,
      background: "#E3F2FD", // light blue
      border: "1.5px solid #1976D2",
      width: 100,
      height: 60,
      fontSize: 12,
    };
  }

  let yOffset = 0;
  if (nodeType === "Syllabus Topics") yOffset = 0;
  else if (nodeType === "Lecture") yOffset = 250;
  else if (nodeType === "Concept") yOffset = 500;

  return {
    id: n.id,
    data: { label, properties, nodeType },
    position: {
      x: typeof savedX === "number" ? savedX : defaultX,
      y: typeof savedY === "number" ? savedY : defaultY + yOffset,
    },
    style: nodeStyle,
  };
};





  // // Load graph from backend API
  // const loadGraphFromBackend = useCallback(async () => {
  //   try {
  //     setIsLoading(true);
  //     console.log("Loading graph from backend API...");

  //     const response = await fetch(`${API_URL}/api/knowledge-graph/export`);
  //     if (!response.ok) {
  //       throw new Error(`Failed to load graph: ${response.status}`);
  //     }

  //     const graphData = await response.json();
  //     console.log("Loaded graph data:", graphData);

  //     // Store the full graph
  //     setAllNodes(graphData.nodes || []);
  //     setAllEdges(graphData.edges || []);

  //     // Set current display
  //     setNodes(graphData.nodes || []);
  //     setEdges(graphData.edges || []);

  //     // Store base for student toggling
  //     setBaseNodes(graphData.nodes || []);
  //     setBaseEdges(graphData.edges || []);

  //     // Fit view after loading
  //     if (reactFlowInstance) {
  //       setTimeout(() => {
  //         reactFlowInstance.fitView({ padding: 0.1 });
  //       }, 100);
  //     }
  //   } catch (error) {
  //     console.error("Error loading graph from backend:", error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // }, [reactFlowInstance, setNodes, setEdges]);

  // Load labels only (not the whole graph at once)
  function filterForTaggedQuestions(graphData) {
  const allowedLabels = ["Student", "StudentQuestion", "SQLConcept"];
  const allowedRels = ["HAS_QUESTION_ATTEMPT", "HAS_ERROR_GROUP", "MENTIONS"];

  const nodes = (graphData.nodes || []).filter(n =>
    (n.labels || []).some(lbl => allowedLabels.includes(lbl))
  );

  const nodeIds = new Set(nodes.map(n => n.id));

  const edges = (graphData.edges || []).filter(
    e =>
      allowedRels.includes(e.type) &&
      nodeIds.has(e.source) &&
      nodeIds.has(e.target)
  );

  return { nodes, edges };
}
  
  
const previousGraphRef = useRef(null);

const loadGraphFromBackend = useCallback(async (graphIdOverride = null) => {
  try {
    setIsLoading(true);

    const gid = graphIdOverride ?? currentGraphId;
    const exportUrl = gid
      ? `${API_URL}/api/knowledge-graph/export?graph_id=${encodeURIComponent(gid)}`
      : `${API_URL}/api/knowledge-graph/export`;

    const [graphRes, labelsRes, relsRes] = await Promise.all([
      fetchWithAuth(exportUrl),
      fetchWithAuth(`${API_URL}/api/knowledge-graph/labels`),
      fetchWithAuth(`${API_URL}/api/knowledge-graph/rels`),
    ]);

    if (!graphRes.ok) throw new Error(`graph ${graphRes.status}`);
    if (!labelsRes.ok) throw new Error(`labels ${labelsRes.status}`);
    if (!relsRes.ok) throw new Error(`rels ${relsRes.status}`);

    const graphData = await graphRes.json();
    const labelsData = await labelsRes.json();
    const relsData = await relsRes.json();

    const metadata = graphData.metadata || {};
    setStudentScopedView(Boolean(metadata.filtered_for_student));
    const nodes = graphData.nodes || [];
    const edges = graphData.edges || [];

    // Snapshot views are already laid out—load verbatim
    if (metadata.source === "snapshot" || (gid && gid.startsWith("snapshot"))) {
      setAllNodes(nodes);
      setAllEdges(edges);
      setBaseNodes(nodes);
      setBaseEdges(edges);
      setStudentNodes([]);
      setStudentEdges([]);
      setNodes(nodes);
      setEdges(edges);
      setLabels(labelsData.labels || []);
      setRels(relsData.rels || relsData.relationships || []);
      if (showStudents) setShowStudents(false);
      reactFlowInstance?.fitView({ padding: 0.1 });
      return;
    }

    // Extract graph parts (non-snapshot path)

    // 🔹 STEP A: Split into student + base nodes
    const { studentNodes, baseNodes, studentEdges, baseEdges } = splitGraph(nodes, edges);

    // 🔹 STEP B: Save all groups for toggling later
    setAllNodes(nodes);
    setAllEdges(edges);
    setBaseNodes(baseNodes);
    setBaseEdges(baseEdges);
    setStudentNodes(studentNodes);
    setStudentEdges(studentEdges);

    // 🔹 STEP C: Initially show base nodes only
    const laidOut = applyLayout(baseNodes, baseEdges, "LR");
    setNodes(laidOut);
    setEdges(baseEdges);

    // 🔹 STEP D: Also load label/relationship lists as before
    setLabels(labelsData.labels || []);
    setRels(relsData.rels || relsData.relationships || []);

    // 🔹 STEP E: Fit the graph to the screen
    reactFlowInstance?.fitView({ padding: 0.1 });

  } catch (e) {
    console.error("loadGraphFromBackend error:", e);
  } finally {
    setIsLoading(false);
  }
}, [API_URL, currentGraphId, reactFlowInstance, showStudents]);

  const toggleAllRelationships = useCallback(async () => {
    if (relsLoading) return;
    if (showAllRelationsActive) {
      const prev = previousGraphRef.current;
      if (prev) {
        setNodes(prev.nodes || []);
        setEdges(prev.edges || []);
        setAllNodes(prev.allNodes || []);
        setAllEdges(prev.allEdges || []);
        setBaseNodes(prev.baseNodes || []);
        setBaseEdges(prev.baseEdges || []);
        setStudentNodes(prev.studentNodes || []);
        setStudentEdges(prev.studentEdges || []);
        if (prev.showStudents != null) setShowStudents(prev.showStudents);
      }
      setShowAllRelationsActive(false);
      setRelsError("");
      previousGraphRef.current = null;
      reactFlowInstance?.fitView({ padding: 0.1 });
      return;
    }

    previousGraphRef.current = {
      nodes,
      edges,
      allNodes,
      allEdges,
      baseNodes,
      baseEdges,
      studentNodes,
      studentEdges,
      showStudents,
    };

    setRelsError("");
    setRelsLoading(true);
    try {
      const relsRes = await fetchWithAuth(`${API_URL}/api/knowledge-graph/rels`);
      if (!relsRes.ok) throw new Error(`rels ${relsRes.status}`);
      const relPayload = await relsRes.json();
      const relTypes = (relPayload.rels || [])
        .map((r) => r.type)
        .filter((type) => typeof type === "string" && type.trim().length);
      if (!relTypes.length) {
        setRelsError("No relationship types available.");
        previousGraphRef.current = null;
        return;
      }
      const queryRes = await fetchWithAuth(`${API_URL}/api/knowledge-graph/query-by-rel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          types: relTypes,
          direction: "out",
          limit: Math.max(5000, relTypes.length * 200),
          skip: 0,
        }),
      });
      if (!queryRes.ok) throw new Error(`query ${queryRes.status}`);
      const payload = await queryRes.json();

      const prevNodesSnapshot = nodes || [];
      const existingIds = new Set(prevNodesSnapshot.map((n) => n.id));
      const newMappedNodes = [];
      (payload.nodes || []).forEach((nodeData, idx) => {
        if (!nodeData?.id) return;
        if (existingIds.has(nodeData.id)) return;
        const mapped = mapApiNode(nodeData, prevNodesSnapshot.length + idx);
        newMappedNodes.push(mapped);
        existingIds.add(nodeData.id);
      });
      const combinedNodes = [...prevNodesSnapshot, ...newMappedNodes];

      const mappedEdges = (payload.edges || []).map(mapApiEdge);

      setAllNodes(combinedNodes);
      setAllEdges(mappedEdges);
      setNodes(combinedNodes);
      setEdges(mappedEdges);
      setBaseNodes(combinedNodes);
      setBaseEdges(mappedEdges);
      setStudentNodes([]);
      setStudentEdges([]);
      if (showStudents) setShowStudents(false);
      setShowAllRelationsActive(true);
      reactFlowInstance?.fitView({ padding: 0.1 });
    } catch (err) {
      setRelsError(err.message || "Failed to load relationships.");
      previousGraphRef.current = null;
      setShowAllRelationsActive(false);
    } finally {
      setRelsLoading(false);
    }
  }, [
    API_URL,
    allEdges,
    allNodes,
    baseEdges,
    baseNodes,
    edges,
    nodes,
    reactFlowInstance,
    relsLoading,
    showAllRelationsActive,
    showStudents,
    studentEdges,
    studentNodes,
  ]);

  const handleSaveSnapshot = useCallback(async () => {
    if (isSnapshotSaving) return;
    try {
      setIsSnapshotSaving(true);
      const trimmedGraphName = (graphName || "").trim();
      const defaultName = `Snapshot ${new Date().toLocaleString()}`;
      const snapshotName = trimmedGraphName || defaultName;
      const snapshotPayload = {
        nodes,
        edges,
        metadata: {
          viewMode: showStudents ? "student" : "concept",
          selectedAssessment: selectedAssessment || null,
          anchorLabels: graphSelectedAnchors,
          graph_name: snapshotName,
        },
      };
      const result = await saveKnowledgeGraphSnapshot(
        undefined,
        snapshotPayload
      );
      const savedAt = result?.metadata?.saved_at;
      const newGraphId = result?.graph_id;
      const targetName = result?.metadata?.graph_name || snapshotName;
      const totalNodes =
        result?.metadata?.total_nodes != null
          ? result.metadata.total_nodes
          : nodes.length;
      const newEntry = {
        id: newGraphId,
        name: targetName,
        node_count: totalNodes,
        draft_nodes: 0,
        source: "snapshot",
        saved_at: savedAt,
        is_snapshot: true,
      };
      if (newGraphId) {
        setGraphs((prev) => {
          const filtered = prev.filter((g) => g.id !== newGraphId);
          return [newEntry, ...filtered];
        });
        setCurrentGraphId(newGraphId);
        setDraftBatchId(null);
        await loadGraphFromBackend(newGraphId);
      }
      setSnapshotMessage(
        savedAt
          ? `Snapshot saved as "${targetName}" @ ${savedAt}`
          : `Snapshot saved as "${targetName}".`
      );
      if (trimmedGraphName) {
        setGraphName("");
      }
    } catch (error) {
      setSnapshotMessage(`Snapshot failed: ${error.message || error}`);
    } finally {
      setIsSnapshotSaving(false);
    }
  }, [
    edges,
    graphName,
    graphSelectedAnchors,
    isSnapshotSaving,
    loadGraphFromBackend,
    nodes,
    selectedAssessment,
    showStudents,
  ]);

  const handleStartSyllabusDraft = useCallback(async () => {
    if (!uploadFiles.length) {
      setDraftError("Please choose a syllabus PDF to upload.");
      return;
    }
    const trimmedGraphName = (graphName || "").trim();
    const syllabusFile = uploadFiles[0];
    setIsUploading(true);
    setDraftError("");
    try {
      const formData = new FormData();
      formData.append("files", syllabusFile);
      const uploadRes = await uploadMaterials(formData);
      const materialId = uploadRes.material_ids?.[0];
      if (!materialId) {
        throw new Error("Upload succeeded but no material ID returned.");
      }
      const draft = await createSyllabusDraft({
        materialId,
        graphName: trimmedGraphName,
      });
      if (draft.graph_name) {
        setGraphName(draft.graph_name);
      }
      mergeCanonicalConcepts(draft.concepts || []);
      setSyllabusDraft(draft);
      setSyllabusDraftConcepts((draft.concepts || []).map((c) => c.label || ""));
    } catch (err) {
      setDraftError(err.message || "Failed to create syllabus draft.");
    } finally {
      setIsUploading(false);
    }
  }, [uploadFiles, graphSelectedAnchors, mergeCanonicalConcepts]);

  const handleCommitSyllabusDraft = useCallback(async () => {
    if (!syllabusDraft) return;
    const trimmed = syllabusDraftConcepts
      .map((label) => (label || "").trim())
      .filter(Boolean);
    if (!trimmed.length) {
      setDraftError("Add at least one concept before committing.");
      return;
    }
    setIsDraftSubmitting(true);
    setDraftError("");
    try {
      await commitSyllabusDraft({
        draftId: syllabusDraft.draft_id,
        concepts: trimmed,
        graphName,
      });
      resetUploadState();
      setShowUploadModal(false);
      await loadGraphFromBackend(null);
      setGraphListReloadTick((tick) => tick + 1);
    } catch (err) {
      setDraftError(err.message || "Failed to commit syllabus draft.");
    } finally {
      setIsDraftSubmitting(false);
    }
  }, [syllabusDraftConcepts, syllabusDraft, resetUploadState, loadGraphFromBackend, setGraphListReloadTick]);

  const handleStartConceptDraft = useCallback(async () => {
    if (!uploadFiles.length) {
      setDraftError("Please choose a concepts file to upload.");
      return;
    }
    setIsUploading(true);
    setDraftError("");
    try {
      const formData = new FormData();
      // Allow uploading multiple files in one request
      uploadFiles.forEach((file) => {
        formData.append("files", file);
      });
      const uploadRes = await uploadMaterials(formData);
      const materialIds = uploadRes.material_ids || [];
      if (!materialIds.length) {
        throw new Error("Upload succeeded but no material IDs returned.");
      }
      const trimmedGraphName = (graphName || "").trim();
      const trimmedDescription = (uploadDescription || "").trim();

      // Create concept drafts for all uploaded materials in parallel
      const draftResults = await Promise.allSettled(
        materialIds.map((materialId) =>
          createConceptDraft({
            materialId,
            graphName: trimmedGraphName,
            description: trimmedDescription || undefined,
          })
        )
      );

      const successfulDrafts = draftResults
        .filter((r) => r.status === "fulfilled")
        .map((r) => r.value);

      if (!successfulDrafts.length) {
        throw new Error("Failed to create concept drafts from uploaded materials.");
      }

      // Use the first successful draft as the primary one,
      // but merge concepts from all successful drafts so the UI stays the same.
      const primaryDraft = successfulDrafts[0];
      const allConcepts = successfulDrafts.flatMap((d) => d.concepts || []);

      if (primaryDraft.graph_name) {
        setGraphName(primaryDraft.graph_name);
      }

      mergeCanonicalConcepts(allConcepts);
      await loadCanonicalConcepts();
      const mergedDraft = { ...primaryDraft, concepts: allConcepts };
      setConceptDraft(mergedDraft);
      setConceptDraftConcepts(
        allConcepts.map(
          (concept) => concept.label || concept.raw_label || ""
        )
      );
      if (graphSelectedAnchors.length) {
        const validAnchors = graphSelectedAnchors.filter((label) =>
          conceptAnchorSet.has((label || "").trim().toLowerCase())
        );
        if (validAnchors.length) {
          setSelectedConceptAnchors((prev) => {
            const merged = mergeAnchors(prev, validAnchors);
            return anchorsEqual(prev, merged) ? prev : merged;
          });
        }
      }
    } catch (err) {
      setDraftError(err.message || "Failed to create concept draft.");
    } finally {
      setIsUploading(false);
    }
  }, [uploadFiles, uploadDescription, graphSelectedAnchors, conceptAnchorSet, mergeAnchors, anchorsEqual, mergeCanonicalConcepts, loadCanonicalConcepts]);

  const handleCommitConceptDraft = useCallback(async () => {
    if (!conceptDraft) return;
    console.log("🔥 FRONTEND: handleCommitConceptDraft() clicked", {
      draftId: conceptDraft?.draft_id,
      conceptDraftConceptsLength: conceptDraftConcepts.length,
      selectedConceptAnchors,
      graphName,
    });
    const cleanedConcepts = conceptDraftConcepts
      .map((label) => (label || "").trim())
      .filter(Boolean);
    if (!cleanedConcepts.length) {
      setDraftError("Add at least one concept before committing.");
      return;
    }
    setIsConceptSubmitting(true);
    setDraftError("");
    try {
      const result = await commitConceptDraft({
        draftId: conceptDraft.draft_id,
        concepts: cleanedConcepts.map((label) => ({ label })),
        anchorLabels: selectedConceptAnchors,
        graphName,
      });
      console.log("🔥 FRONTEND: handleCommitConceptDraft() got data =", result);
      console.log("🔥 FRONTEND: handleCommitConceptDraft() result", {
        batchId: result?.batch_id,
        conceptCount: Array.isArray(result?.concepts) ? result.concepts.length : null,
        graphName: result?.graph_name,
      });
      const batchId = result?.batch_id || null;
      const curatedLabels = (result?.concepts || []).map((c) => c.label).filter(Boolean);
      // Close modal & reset upload state
      resetUploadState();
      setShowUploadModal(false);
      if (batchId) setDraftBatchId(batchId);
      // Navigate to EdgeReview (step 3) instead of loading graph immediately
      navigate("/edge-review", {
        state: {
          concepts: curatedLabels,
          batchId,
          graphName: result?.graph_name || graphName || "",
          initialEdges: result?.edges || [],
          initialAgents: result?.agents || [],
        },
      });
    } catch (err) {
      setDraftError(err.message || "Failed to commit concept draft.");
    } finally {
      setIsConceptSubmitting(false);
    }
  }, [
    conceptDraft,
    conceptDraftConcepts,
    selectedConceptAnchors,
    resetUploadState,
    loadGraphFromBackend,
    setCurrentGraphId,
    setDraftBatchId,
    setGraphListReloadTick,
  ]);



  const loadByLabel = async (label) => {
    try {
      const res = await fetchWithAuth(`${API_URL}/api/knowledge-graph/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ labels: [label], limit: 50 }),
      });
      const data = await res.json();

      // ✅ STEP 1: Filter only Student nodes if label == "Student"
      let filteredNodes = data.nodes || [];
      if (label === "Student") {
        filteredNodes = filteredNodes.filter(
          (n) =>
            n.labels?.includes("Student") &&
            !n.labels.includes("StudentAssessment") &&
            !n.labels.includes("StudentQuestion") &&
            !n.labels.includes("StudentError")
        );
      }

      // ✅ STEP 2: Convert to ReactFlow nodes and edges
      const rfNodes = filteredNodes.map(mapApiNode);
      const rfEdges = (data.edges || []).map(mapApiEdge);

      // ✅ STEP 3: Replace the current graph view with the filtered set
      setNodes(rfNodes);
      setEdges(rfEdges);
    } catch (err) {
      console.error("Error loading label:", err);
    }
  };


  const loadByRel = async (type) => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/knowledge-graph/query-by-rel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          types: [type],
          direction: "any", // 'out' | 'in' | 'any'
          limit: 25,
          skip: 0,
        }),
      });
      const data = await res.json();

      const rfNodes = (data.nodes || []).map((n, i) => mapApiNode(n, i));

      const rfEdges = (data.edges || []).map(mapApiEdge);

    setNodes(rfNodes);
    setEdges(rfEdges);
    setSelectedNode(null);
    reactFlowInstance?.fitView({ padding: 0.1 });
  } catch (e) {
    console.error("loadByRel error:", e);
  } finally {
    setIsLoading(false);
  }
};

const loadCourseView = async () => {
  try {
    // Fetch Students, Documents, and Concepts in parallel
    const [studentsRes, documentsRes, conceptsRes] = await Promise.all([
      fetchWithAuth(`${API_URL}/api/knowledge-graph/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ labels: ["Student"], limit: 50 }),
      }),
      fetchWithAuth(`${API_URL}/api/knowledge-graph/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ labels: ["Document"], limit: 50 }),
      }),
      fetchWithAuth(`${API_URL}/api/knowledge-graph/query`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // tweak the limit if your concept set is large
        body: JSON.stringify({ labels: ["Concept"], limit: 200 }),
      }),
    ]);

    // Parse JSON results together
    const [studentsData, documentsData, conceptsData] = await Promise.all([
      studentsRes.json(),
      documentsRes.json(),
      conceptsRes.json(),
    ]);

    // --- Filter nodes by label (with a fallback for 'type' props) ---
    const studentNodes = (studentsData.nodes || []).filter(
      (n) =>
        n.labels?.includes("Student") &&
        !n.labels.includes("StudentAssessment") &&
        !n.labels.includes("StudentQuestion") &&
        !n.labels.includes("StudentError")
    );

    const documentNodes = (documentsData.nodes || []).filter((n) =>
      n.labels?.includes("Document")
    );

    const conceptNodes = (conceptsData.nodes || []).filter(
      (n) =>
        n.labels?.includes("Concept") ||
        n.type === "Concept" ||
        n.properties?.type === "Concept"
    );

    // --- Merge + de-duplicate nodes/edges ---
    const combinedNodesRaw = [...studentNodes, ...documentNodes, ...conceptNodes];
    const combinedEdgesRaw = [
      ...(studentsData.edges || []),
      ...(documentsData.edges || []),
      ...(conceptsData.edges || []),
    ];

    // Deduplicate nodes by id
    const nodeMap = new Map();
    for (const n of combinedNodesRaw) {
      if (!n?.id) continue;
      if (!nodeMap.has(n.id)) nodeMap.set(n.id, n);
    }
    const uniqueNodes = Array.from(nodeMap.values());

    // Deduplicate edges by id or (source-target-type/label) signature
    const seenEdges = new Set();
    const uniqueEdges = combinedEdgesRaw.filter((e) => {
      if (!e) return false;
      const key =
        e.id ??
        `${e.source || e.start}-${e.target || e.end}-${e.type || e.label || ""}`;
      if (seenEdges.has(key)) return false;
      seenEdges.add(key);
      return true;
    });

    // Convert for React Flow
    const rfNodes = uniqueNodes.map(mapApiNode);
    const rfEdges = uniqueEdges.map(mapApiEdge);

    // Render together
    setNodes(rfNodes);
    setEdges(rfEdges);
    reactFlowInstance?.fitView({ padding: 0.1 });

    console.log(
      `📘 Course View loaded: ${rfNodes.length} nodes, ${rfEdges.length} edges (incl. Concepts)`
    );
  } catch (error) {
    console.error("❌ Error loading Course View:", error);
  }
};




  // Load ONLY once on component mount
  useEffect(() => {
    if (hasLoadedInitially.current) return;
    hasLoadedInitially.current = true;

    const params = new URLSearchParams(location.search || "");
    const urlGraphId = params.get("graph_id");

    if (urlGraphId) {
      setCurrentGraphId(urlGraphId);
      setDraftBatchId(urlGraphId);
      loadGraphFromBackend(urlGraphId);
      return;
    }

    loadGraphFromBackend();
  }, [location.search, loadGraphFromBackend]);

  useEffect(() => {
    let cancelled = false;
    fetchWithAuth(`${API_URL}/api/user`)
      .then((res) => {
        if (!res.ok) throw new Error(`user ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setCurrentUser(data);
        setSelectedNetId((prev) => prev || data?.net_id || data?.netId || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("Failed to load current user", err);
        setCurrentUser(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load graph list
  useEffect(() => {
    (async () => {
      try {
        const res = await listGraphs();
        setGraphs(res.graphs || []);
      } catch (e) {
        console.warn("Failed to list graphs:", e);
      }
    })();
  }, []);

  // Load materials list
  useEffect(() => {
    (async () => {
      try {
        const res = await listMaterials();
        setMaterials(res.materials || []);
      } catch (e) {
        console.warn("Failed to list materials:", e);
      }
    })();
  }, []);

  // (Disabled) student performance overview fetch
  useEffect(() => {
    if (!ENABLE_STUDENT_PERFORMANCE) return;
    if (!selectedNetId) return;

    console.log(
      "🔥 FRONTEND: Fetching student performance for netId:",
      selectedNetId
    );

    axios
      .get(`${API_URL}/api/student-performance-overview`, {
        params: { net_id: selectedNetId },
        headers: getAuthHeaders(),
      })
      .then((res) => {
        console.log("🔥 FRONTEND: Student performance data:", res.data);
        setProfile(res.data.profile);
        setAssessmentErrors(res.data.assessment_errors);
      })
      .catch((err) => {
        console.error("🔥 FRONTEND: Error fetching student performance:", err);
      });
  }, [selectedNetId]);

  // Pull new submissions into Neo4j, then reload the graph
  async function ingestAndRefresh() {
    console.log(
      "🔥 FRONTEND: ingestAndRefresh() called - starting student data ingestion"
    );
    setIsLoading(true);
    try {
      console.log(
        "🔥 FRONTEND: About to call backend API for ingesting submissions"
      );
      console.log(
        "🔥 FRONTEND: API URL:",
        `${API_URL}/api/knowledge-graph/ingest-submissions`
      );
      
      const response = await fetchWithAuth(
        `${API_URL}/api/knowledge-graph/ingest-submissions`,
        {
          method: "POST",
        }
      );

      console.log(
        "🔥 FRONTEND: Backend ingest API response status:",
        response.status
      );

      if (!response.ok) {
        throw new Error(`Backend API failed with status: ${response.status}`);
      }

      const ingestResult = await response.json();
      console.log("🔥 FRONTEND: Backend ingest result:", ingestResult);

      console.log("🔥 FRONTEND: Now fetching graph data...");

      // re-fetch the full graph
      const graph = await loadKnowledgeGraph(currentGraphId || undefined);
      console.log("🔥 FRONTEND: Loaded graph data:", graph);

      const partitions = splitGraph(graph);
      setAllNodes(graph.nodes || []);
      setAllEdges(graph.edges || []);
      setBaseNodes(partitions.baseNodes);
      setStudentNodes(partitions.studentNodes);
      setBaseEdges(partitions.baseEdges);
      setStudentEdges(partitions.studentEdges);
      const combinedNodes = showStudents
        ? [...partitions.baseNodes, ...partitions.studentNodes]
        : partitions.baseNodes;
      const combinedEdges = showStudents
        ? [...partitions.baseEdges, ...partitions.studentEdges]
        : partitions.baseEdges;
      setNodes(combinedNodes);
      setEdges(combinedEdges);

      console.log("🔥 FRONTEND: ingestAndRefresh() completed successfully");
    } catch (err) {
      console.error("🔥 FRONTEND: Error in ingestAndRefresh():", err);
    } finally {
      setIsLoading(false);
    }
  }

  // expand on double-click (ring layout around clicked node)
  const onNodeDoubleClick = useCallback(
    async (_, node) => {
      try {
        const res = await fetchWithAuth(
          `${API_URL}/api/knowledge-graph/expand?node_id=${encodeURIComponent(
            node.id
          )}&limit=25`
        );
        const { nodes: nn = [], edges: ee = [] } = await res.json();

        const existingN = new Set((nodes || []).map((n) => n.id));
        const existingE = new Set((edges || []).map((e) => e.id));

        // new nodes only, mapped to RF nodes
        const newNodes = nn.filter((n) => !existingN.has(n.id)).map(mapApiNode);

        // place them in a ring around the clicked node
        const cx = node.position?.x ?? 0;
        const cy = node.position?.y ?? 0;
        const R = 180; // radius of the ring
        const m = Math.max(newNodes.length, 1);

        const arranged = newNodes.map((n, i) => ({
          ...n,
          position: {
            x: cx + R * Math.cos((2 * Math.PI * i) / m),
            y: cy + R * Math.sin((2 * Math.PI * i) / m),
          },
        }));

        const newEdges = ee.filter((e) => !existingE.has(e.id)).map(mapApiEdge);

        setNodes((prev) => [...prev, ...arranged]);
        setEdges((prev) => [...prev, ...newEdges]);
      } catch (e) {
        console.error("expand error:", e);
      }
    },
    [nodes, edges, setNodes, setEdges]
  );

  // expand on right-click
  const onNodeContextMenu = useCallback(
    (evt, node) => {
      evt.preventDefault();
      const childEdges = allEdges.filter((e) => e.source === node.id);
      const childNodes = childEdges
        .map((e) => allNodes.find((n) => n.id === e.target))
        .filter((n) => n && !nodes.some((existing) => existing.id === n.id));

      setEdges((prev) => [...prev, ...childEdges]);
      setNodes((prev) => [...prev, ...childNodes]);
    },
    [allEdges, allNodes, nodes, setEdges, setNodes]
  );

  const onConnect = useCallback(
    (params) => {
      const e = {
        ...params,
        id: `e-${params.source}-${params.target}`,
        animated: false,
        label: "relates to",
        labelStyle: { fill: "#888", fontWeight: 700 },
        markerEnd: { type: MarkerType.ArrowClosed },
      };
      setEdges((es) => addEdge(e, es));
    },
    [setEdges]
  );

  const onNodeDragStart = useCallback((_, node) => {
    const sim = simRef.current;
    if (!sim) return;
    const s = sim.nodes().find((n) => n.id === node.id);
    if (s) {
      s.fx = node.position.x;
      s.fy = node.position.y;
    }
    sim.alphaTarget(0.3).restart();
  }, []);

  const onNodeDrag = useMemo(
    () =>
      throttle((_, node) => {
        const sim = simRef.current;
        if (!sim) return;
        const s = sim.nodes().find((n) => n.id === node.id);
        if (s) {
          s.fx = node.position.x;
          s.fy = node.position.y;
        }
        setNodes((ns) =>
          ns.map((n) =>
            n.id === node.id ? { ...n, position: node.position } : n
          )
        );
      }, 16),
    [setNodes]
  );

  const onNodeDragStop = useCallback((_, node) => {
    const sim = simRef.current;
    if (!sim) return;
    const s = sim.nodes().find((n) => n.id === node.id);
    if (s) {
      s.fx = null;
      s.fy = null;
    } // release back to physics
    sim.alphaTarget(0);
  }, []);

  // Updated onNodeClick to handle student selection properly
  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);

    console.log("🔥 FRONTEND: Node clicked:", node);
    console.log("🔥 FRONTEND: Node data:", node.data);
    console.log("🔥 FRONTEND: Node properties:", node.data.properties);
    console.log("🔥 FRONTEND: Node type:", node.data.nodeType);

    // Check if node is a student and extract netid
    let netId = null;

    if (node.data.nodeType === "Student") {
      // For Student nodes, get netid from properties
      netId = node.data.properties?.netid;
      console.log("🔥 FRONTEND: Found Student node with netId:", netId);
    } else if (
      node.data.nodeType === "StudentAssessment" ||
      node.data.nodeType === "StudentQuestion" ||
      node.data.nodeType === "StudentError"
    ) {
      // For student-specific nodes, get netid from student_netid property
      netId = node.data.properties?.student_netid;
      console.log(
        "🔥 FRONTEND: Found student-specific node with netId:",
        netId
      );
    } else if (node.data.properties?.netid) {
      // Fallback: check if any node has netid property
      netId = node.data.properties.netid;
      console.log("🔥 FRONTEND: Found netid in properties:", netId);
    }

    if (ENABLE_STUDENT_PERFORMANCE) {
      setSelectedNetId(netId || null);
      console.log("🔥 FRONTEND: Set selectedNetId to:", netId);
    }
  }, []);

  const onEdgeClick = useCallback((evt, edge) => {
    evt.preventDefault();
    setEdgeToEdit(edge);
    setEdgeLabelDraft(edge.label);
  }, []);

  const exportGraph = useCallback(() => {
    const data = JSON.stringify({ nodes, edges }, null, 2);
    const uri =
      "data:application/json;charset=utf-8," + encodeURIComponent(data);
    const a = document.createElement("a");
    a.href = uri;
    a.download = "sql-knowledge-graph.json";
    a.click();
  }, [nodes, edges]);

  const clearGraph = useCallback(() => {
    setNodes([]);
    setEdges([]);
  }, [setNodes, setEdges]);

  const handleMultiFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setIsUploading(true);
    setUploadFiles(files);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append(`files`, file);
      });

      // console.log(`Uploading ${files.length} files...`);
      // const response = await uploadMultiplePdfsAndGetGraph(formData);
      // console.log("🚀 Raw response from server:", response);

      // let graphData = response;
      // if (response.nodes && response.edges) {
      //   graphData = response;
      // } else if (response.status === "success") {
      //   console.log("Created ontology, now fetching graph data...");
      //   const graphResponse = await fetch(
      //     `${API_URL}/api/knowledge-graph/sql-concepts`
      //   );
      //   graphData = await graphResponse.json();

      console.log(`Uploading ${files.length} files as persisted materials...`);
      const uploadRes = await uploadMaterials(formData); // { material_ids }
      console.log("🚀 Upload result:", uploadRes);

      if (!uploadRes?.material_ids?.length) {
        throw new Error("No material_ids returned from upload");
      }

      console.log("Extracting draft from materials...", uploadRes.material_ids);
      const draft = await extractDraft(uploadRes.material_ids, {
        assessmentId: selectedAssessment,
        autoTag: Boolean(selectedAssessment),
        graphName,
      }); // { batch_id, nodes, edges }
      console.log("🚀 Draft graph:", draft);

      setDraftBatchId(draft.batch_id || null);
      setCurrentGraphId(draft.batch_id || null);
      setAllNodes(draft.nodes || []);
      setAllEdges(draft.edges || []);
      setNodes(draft.nodes || []);
      setEdges(draft.edges || []);
      setBaseNodes(draft.nodes || []);
      setBaseEdges(draft.edges || []);

      if (reactFlowInstance) {
        reactFlowInstance.fitView({ padding: 0.1 });
      }

      const count = draft.metadata?.total_nodes || draft.nodes?.length || 0;
      let message = `✅ Draft created from ${files.length} files. Nodes: ${count}`;
      if (draft.tagging_started) {
        message += `\nTagging questions for ${selectedAssessment || "assessment"}…`;
      }
      alert(message);
      setGraphName("");

      // refresh graph list
      try {
        const res = await listGraphs();
        setGraphs(res.graphs || []);
      } catch {}
      try {
        const m = await listMaterials();
        setMaterials(m.materials || []);
      } catch {}
      refreshProgressInsights(selectedAssessment);
    } catch (error) {
      console.error("Multi-file upload failed:", error);
      alert(`❌ Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
      setUploadFiles([]);
    }
  };

  const handleSaveGraph = async () => {
    setIsLoading(true);
    try {
      console.log("Saving current graph state...");
      const response = await fetchWithAuth(
        `${API_URL}/api/knowledge-graph/save-changes`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nodes,
            edges,
            graph_id: currentGraphId || null,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Save failed: ${response.status}`);
      }

      const result = await response.json();
      console.log("Save result:", result);
      const contextLabel = currentGraphId ? `draft ${currentGraphId}` : "main graph";
      alert(`✅ Saved ${contextLabel} (nodes: ${nodes.length}, edges: ${edges.length})`);
    } catch (error) {
      console.error("Save error:", error);
      alert(`❌ Failed to save graph to Neo4j: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscardDraft = async () => {
    if (!draftBatchId) return alert("No draft to discard.");
    if (!window.confirm("Discard this draft? This will delete draft nodes."))
      return;
    setIsLoading(true);
    try {
      await discardDraft(draftBatchId);
      setDraftBatchId(null);
      setCurrentGraphId(null);
      // Clear current view
      setNodes([]);
      setEdges([]);
      alert("✅ Draft discarded.");
      // refresh graph list
      try {
        const res = await listGraphs();
        setGraphs(res.graphs || []);
      } catch {}
    } catch (e) {
      alert(`❌ Discard failed: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openNodeEditor = () => {
    // Open modal for edit (if selected) or create (if none selected)
    setShowNodeEdit(true);
  };

  const updateNodeInline = (id, payload) => {
    setNodes((ns) =>
      ns.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...payload } } : n
      )
    );
    setAllNodes((ns) =>
      ns.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...payload } } : n
      )
    );
  };

  const deleteNodeInline = (id) => {
    setEdges((es) => es.filter((e) => e.source !== id && e.target !== id));
    setNodes((ns) => ns.filter((n) => n.id !== id));
    setAllNodes((ns) => ns.filter((n) => n.id !== id));
    if (selectedNode && selectedNode.id === id) setSelectedNode(null);
  };

  useEffect(() => {
    async function fetchAssessments() {
      setAssessmentsLoading(true);
      try {
        const response = await fetchWithAuth(`${API_URL}/api/assessments`);
        if (!response.ok) throw new Error("Failed to load assessments");
        const data = await response.json();
        setAssessmentOptions(data.assessments.map((a) => a.id));
        // Prefer Quiz-practice11, else first
        if (data.assessments.some((a) => a.id === "Quiz-practice11")) {
          setSelectedAssessment("Quiz-practice11");
          await refreshProgressInsights("Quiz-practice11");
        } else if (data.assessments.length > 0) {
          const first = data.assessments[0].id;
          setSelectedAssessment(first);
          await refreshProgressInsights(first);
        } else {
          await refreshProgressInsights("");
        }
      } catch (err) {
        setAssessmentOptions([]);
        setSelectedAssessment("");
        await refreshProgressInsights("");
      } finally {
        setAssessmentsLoading(false);
      }
    }
    fetchAssessments();
  }, [refreshProgressInsights]);

  return (
    <div className="knowledge-graph-container">
      <div className="knowledge-graph-header">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h1>SQL Knowledge Graph Editor</h1>
            <p>Visualize & edit SQL topic relations</p>
            {studentScopedView && currentUser?.role !== "instructor" && (
              <p style={{ fontSize: "12px", color: "#047857", marginTop: "-6px" }}>
                You are viewing your personal progress; other students’ data is hidden.
              </p>
            )}
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => setLeftPanelVisible(!leftPanelVisible)}
              className="knowledge-graph-button knowledge-graph-button-outline"
              style={{ fontSize: "12px", padding: "6px 12px" }}
            >
              {leftPanelVisible ? "◀ Hide Controls" : "▶ Show Controls"}
            </button>
            <button
              onClick={() => setRightPanelVisible(!rightPanelVisible)}
              className="knowledge-graph-button knowledge-graph-button-outline"
              style={{ fontSize: "12px", padding: "6px 12px" }}
            >
              {rightPanelVisible ? "▶ Hide Details" : "◀ Show Details"}
            </button>
          </div>
        </div>
      </div>

      <div className="knowledge-graph-content">
        {/* LEFT sidebar */}
        {leftPanelVisible && (
          <div className="knowledge-graph-sidebar">
            {/* Explore by Label */}
            <div className="knowledge-graph-card">
              <div className="knowledge-graph-card-header">
                <h2>Explore by Label</h2>
              </div>
              <div className="kg-chip-list">
                {labels.length === 0 ? (
                  <p style={{ fontSize: 12 }}>No labels loaded yet.</p>
                ) : (
                  <>
                    {labels.slice(0, labelLimit).map((l) => (
                      <button
                        key={l.label}
                        onClick={() => loadByLabel(l.label)}
                        className="kg-chip"
                      >
                        {l.label} <span className="count">({l.count})</span>
                      </button>
                    ))}
                    {/* --- Custom combined Course View button --- */}
                    <button
                      onClick={loadCourseView}
                      className="knowledge-graph-button"
                      style={{
                        marginTop: "10px",
                        marginBottom: "10px",
                        backgroundColor: "#2563eb",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      📘 Course View
                    </button>

                    <button
                    onClick={() => setShowUploadModal(true)}
                    className="knowledge-graph-button"
                    style={{ marginBottom: "10px" }}
                  >
                    ➕ Upload Material
                  </button>
                    {labels.length > labelLimit && (
                      <button
                        className="kg-more"
                        onClick={() => setLabelLimit((v) => v + PAGE)}
                      >
                        View more
                      </button>
                    )}
                    {labels.length > labelLimit && (
                  <button className="kg-more" onClick={() => setLabelLimit(v => v + PAGE)}>
                    View more
                  </button>
                )}
                {labelLimit > PAGE && (
                  <button className="kg-more" onClick={() => setLabelLimit(PAGE)}>
                    Show less
                  </button>
                )}
              </>
            )}
          </div>
          </div>

            {/* Explore by Relationship */}
            <div className="knowledge-graph-card">
              <div className="knowledge-graph-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <h2 style={{ margin: 0 }}>Explore by Relationship</h2>
                <button
                  className="knowledge-graph-button knowledge-graph-button-outline"
                  onClick={toggleAllRelationships}
                  disabled={relsLoading}
                  style={{ fontSize: 12, padding: "6px 10px" }}
                >
                  {relsLoading
                    ? "Loading…"
                    : showAllRelationsActive
                    ? "Hide All"
                    : "Show All"}
                </button>
              </div>
              <div className="knowledge-graph-card-content">
                {relsError && (
                  <div style={{ color: "#b91c1c", fontSize: 12, marginBottom: 6 }}>
                    {relsError}
                  </div>
                )}
                <div className="kg-chip-list">
                  {rels.length === 0 ? (
                    <p style={{ fontSize: 12, color: "#667085" }}>
                      No relationships loaded yet.
                    </p>
                  ) : (
                    <>
                      {rels.slice(0, relLimit).map((r) => (
                        <button
                          key={r.type}
                          onClick={() => loadByRel(r.type)}
                          className="kg-chip"
                          title={r.type}
                        >
                          {r.type} <span className="count">({r.count})</span>
                        </button>
                      ))}

                      {rels.length > relLimit && (
                        <button
                          className="kg-more"
                          onClick={() => setRelLimit((v) => v + PAGE)}
                        >
                          View more
                        </button>
                      )}
                      {relLimit > PAGE && (
                        <button
                          className="kg-more"
                          onClick={() => setRelLimit(PAGE)}
                        >
                          Show less
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Removed separate Add Topic card; use unified Node Editor */}

            <button
          onClick={async () => {
            console.log("🔥 FRONTEND: Toggling Student Data view...");
            if (!showStudents && studentNodes.length === 0) {
              console.log("⚙️ No student data loaded yet — calling ingestAndRefresh()");
              await ingestAndRefresh();
            }
            setShowStudents((prev) => !prev);
          }}
          className="knowledge-graph-button"
          disabled={isLoading}
          style={{ marginBottom: "10px" }}
        >
          {showStudents ? "Hide Student Data" : "🎓 Show Student Data"}
        </button>


            {/* Add helper text */}
            {showStudents && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#666",
                  marginBottom: "10px",
                  padding: "8px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "4px",
                  border: "1px solid #e9ecef",
                }}
              >
                <strong>Student Journey View Active</strong>
                <br />
                Each student has their own horizontal journey:
                <br />
                Student → Assessment → Question → Error
                <br />
                <em>Students are grouped in separate rows</em>
              </div>
            )}

          {/* Assessment Selector */}
          <div style={{ marginBottom: "10px" }}>
            <label htmlFor="assessment-select" style={{ fontWeight: "bold", marginRight: 8 }}>
              Assessment:
            </label>
            <select
              id="assessment-select"
              value={selectedAssessment}
              onChange={async (e) => {
                const val = e.target.value;
                setSelectedAssessment(val);
                await refreshProgressInsights(val);
              }}
              disabled={isLoading || assessmentsLoading || assessmentOptions.length === 0}
              style={{ padding: "4px 8px", borderRadius: 4 }}
            >
              {assessmentsLoading ? (
                <option>Loading...</option>
              ) : assessmentOptions.length === 0 ? (
                <option>No assessments found</option>
              ) : (
                assessmentOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))
              )}
            </select>
          </div>

           {/* 🏷️ Tag Questions Button (toggle-aware) */}
       <button
        onClick={async () => {
          console.log("🔥 FRONTEND: Tag Questions button clicked!");
          setIsLoading(true);
          try {
            // 1) Toggle tagging (create if none, remove if exists)
            const resp = await fetchWithAuth(`${API_URL}/api/knowledge-graph/tag-student-questions`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
            });
            if (!resp.ok) throw new Error(`Failed to toggle tagging: ${resp.status}`);
            const result = await resp.json();
            console.log("🔥 FRONTEND: Tagging toggle result:", result);

            // 2) Reload graph from Neo4j
            const graphRes = await fetchWithAuth(`${API_URL}/api/knowledge-graph/load-from-neo4j`);
            if (!graphRes.ok) throw new Error(`Failed to load graph: ${graphRes.status}`);
            const loaded = await graphRes.json();
            const graphData = loaded?.data ?? loaded;

            // 3) Push into React Flow
            const nextNodes = (graphData?.nodes ?? []).map((n, i) => mapApiNode(n, i));
            const nextEdges = (graphData?.edges ?? []).map(mapApiEdge);
            setNodes(nextNodes);
            setEdges(nextEdges);
            reactFlowInstance?.fitView?.({ padding: 0.1 });

            // 4) Friendly message depending on toggle outcome
            if (result?.status === "created") {
              alert(result?.message ? `✅ ${result.message}` : "✅ Created MENTIONS edges.");
            } else if (result?.status === "removed") {
              alert(result?.message ? `🧹 ${result.message}` : "🧹 Removed MENTIONS edges.");
            } else {
              const qp = result?.questions_processed ?? "some";
              const tl = result?.total_links ?? result?.created ?? result?.deleted ?? "some";
              alert(`ℹ️ Tagging completed. Processed ${qp} questions, ${tl} edges affected.`);
            }
          } catch (error) {
            console.error("🔥 FRONTEND: Error toggling question tags:", error);
            alert(`❌ Failed: ${error.message ?? error}`);
          } finally {
            setIsLoading(false);
          }
        }}
        className="knowledge-graph-button"
        disabled={isLoading}
        style={{ marginBottom: "10px" }}
      >
        🏷️ Tag Student Questions
      </button>

          {/* NEW: Track Progress Button */}
          <button
            onClick={async () => {
              console.log("🔥 FRONTEND: Track Progress button clicked!");
              setIsLoading(true);
              try {
                const response = await fetchWithAuth(
                  `${API_URL}/api/knowledge-graph/track-progress`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ assessment_id: selectedAssessment })
                  }
                );
                if (!response.ok) {
                  throw new Error(
                    `Failed to track progress: ${response.status}`
                  );
                }
                const result = await response.json();
                console.log("🔥 FRONTEND: Track progress result:", result);
                setProgressSummary(result);
                setProgressStatus("fresh");
                setNodes((currentNodes) =>
                  currentNodes.map((node) => {
                    const labelRaw = node.data?.label ?? "";
                    const conceptKey = labelRaw.toUpperCase();
                    const progressData =
                      result.concept_progress[conceptKey] ||
                      result.concept_progress[labelRaw] ||
                      result.concept_progress[labelRaw.replace(/_/g, " ")] ||
                      result.concept_progress[labelRaw.replace(/\s+/g, " ").toUpperCase()];
                    if (progressData) {
                      const percentage = progressData.percentage;
                      let backgroundColor = "#f0f0f0"; // default gray
                      if (percentage <= 20) {
                        backgroundColor = "#ff6b6b"; // red
                      } else if (percentage <= 40) {
                        backgroundColor = "#ffa726"; // orange
                      } else if (percentage <= 60) {
                        backgroundColor = "#ffeb3b"; // yellow
                      } else if (percentage <= 80) {
                        backgroundColor = "#81c784"; // light green
                      } else {
                        backgroundColor = "#4caf50"; // dark green
                      }
                      return {
                        ...node,
                        style: {
                          ...node.style,
                          backgroundColor,
                          border: `3px solid ${backgroundColor}`,
                        },
                        data: {
                          ...node.data,
                          progress: progressData,
                        },
                      };
                    }
                    return node;
                  })
                );
                alert(
                  `✅ Progress tracking complete!\n${result.total_students} students, ${result.concepts_tracked} concepts tracked\nNodes colored by completion percentage`
                );
                if (selectedAssessment) {
                  await refreshProgressInsights(selectedAssessment);
                }
              } catch (error) {
                console.error("🔥 FRONTEND: Error tracking progress:", error);
                alert(`❌ Failed to track progress: ${error.message}`);
              } finally {
                setIsLoading(false);
              }
            }}
            className="knowledge-graph-button"
            disabled={isLoading || assessmentsLoading || !selectedAssessment}
          >
            📊 Track Progress
          </button>

          {progressSummary && (
            <div className="knowledge-graph-card">
              <div className="knowledge-graph-card-header">
                <h2>📈 Concept Insights</h2>
              </div>
              <div
                className="knowledge-graph-card-content"
                style={{ display: "flex", flexDirection: "column", gap: "12px" }}
              >
                <div>
                  <strong>Students:</strong> {progressSummary.total_students || 0}
                  {progressSummary.class_summary && (
                    <span style={{ marginLeft: 12 }}>
                      <strong>Avg:</strong>{" "}
                      {progressSummary.class_summary.average_completion ?? 0}%
                      <span style={{ marginLeft: 8 }}>
                        <strong>Median:</strong>{" "}
                        {progressSummary.class_summary.median_completion ?? 0}%
                      </span>
                    </span>
                  )}
                  {progressStatus === "cached" && (
                    <span style={{ marginLeft: 12, color: "#888" }}>(cached)</span>
                  )}
                  {progressStatus === "error" && (
                    <span style={{ marginLeft: 12, color: "#d9534f" }}>⚠️ Refresh failed</span>
                  )}
                </div>

                {progressSummary.class_summary &&
                  progressSummary.class_summary.top_concept_gaps?.length > 0 && (
                    <div>
                      <strong>Top Concept Gaps</strong>
                      <ul style={{ margin: "6px 0 0 16px" }}>
                        {progressSummary.class_summary.top_concept_gaps.map((gap) => (
                          <li key={gap.concept}>
                            {gap.concept}: {gap.percentage}% ({gap.completed}/{gap.total_possible})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {progressSummary.student_summary &&
                  progressSummary.student_summary.top_students?.length > 0 && (
                    <div>
                      <strong>Top Students</strong>
                      <ul style={{ margin: "6px 0 0 16px" }}>
                        {progressSummary.student_summary.top_students.map((student) => (
                          <li key={student.net_id}>
                            {student.net_id}: {student.mastery_percentage}% mastered
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {progressSummary.class_summary && (
                  <div style={{ fontSize: "12px", color: "#666" }}>
                    Generated at {progressSummary.generated_at?.replace("T", " ")?.replace("Z", " UTC")}
                  </div>
                )}
              </div>
            </div>
          )}

            {/* Unified Node Editor (create/edit) */}
            <button
              onClick={openNodeEditor}
              className="knowledge-graph-button"
              style={{
                marginBottom: "10px",
                display: "inline-block",
                whiteSpace: "nowrap",
              }}
              title={selectedNode ? "Edit selected node" : "Create a new topic"}
            >
              ✏️ Node Editor
            </button>

            {draftBatchId && (
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <button
                  onClick={handleDiscardDraft}
                  className="knowledge-graph-button"
                >
                  🗑️ Delete Graph
                </button>
              </div>
            )}

          {/* Graph selector */}
          <div className="knowledge-graph-card" style={{ marginTop: 10 }}>
            <div className="knowledge-graph-card-header">
              <h2>🗂️ Graphs</h2>
            </div>
            <div className="knowledge-graph-card-content">
              <select
                className="knowledge-graph-input"
                value={currentGraphId || "__MAIN__"}
                onChange={async (e) => {
                  const val = e.target.value === "__MAIN__" ? null : e.target.value;
                  setCurrentGraphId(val);
                  if (val) {
                    const selected = graphs.find((g) => g.id === val);
                    if (selected?.is_snapshot) {
                      setDraftBatchId(null);
                    } else {
                      setDraftBatchId(val);
                    }
                  } else {
                    setDraftBatchId(null);
                  }
                  const graph = await loadKnowledgeGraph(val || undefined);
                  setAllNodes(graph.nodes || []);
                  setAllEdges(graph.edges || []);
                  setNodes(graph.nodes || []);
                  setEdges(graph.edges || []);
                  // reset base for student merge and exit student view to avoid stale merge
                  setBaseNodes(graph.nodes || []);
                  setBaseEdges(graph.edges || []);
                  if (showStudents) setShowStudents(false);
                  reactFlowInstance?.fitView({ padding: 0.1 });
                }}
              >
                <option value="__MAIN__">Main Graph</option>
                {graphs.map((g) => {
                  const optionLabel = g.name ? `${g.name} (${g.id})` : g.id;
                  const prefix = g.is_snapshot ? "🗂️ " : "";
                  const suffix = g.draft_nodes ? ` (draft nodes: ${g.draft_nodes})` : "";
                  const display = `${prefix}${optionLabel}${suffix}`;
                  return (
                    <option key={g.id} value={g.id}>
                      {display}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* Canonical concept review */}
          {false && (
            <div className="knowledge-graph-card" style={{ marginTop: 10 }}>
              {/* Concept review card disabled */}
            </div>
          )}

          {false && (
          <>
          Materials selector
          <div className="knowledge-graph-card" style={{ marginTop: 10 }}>
            <div className="knowledge-graph-card-header">
              <h2>📂 Materials</h2>
            </div>
            <div className="knowledge-graph-card-content">
              {materials.length === 0 ? (
                <div style={{ fontSize: 12, color: "#666" }}>No materials uploaded yet.</div>
              ) : (
                <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #eee", padding: 8, borderRadius: 4 }}>
                  {materials.map((m) => (
                    <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginBottom: 6 }}>
                      <input
                        type="checkbox"
                        checked={selectedMaterialIds.includes(m.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedMaterialIds((prev) =>
                            checked ? [...prev, m.id] : prev.filter((x) => x !== m.id)
                          );
                        }}
                      />
                      <span style={{ flex: 1 }}>{m.filename}</span>
                      <span style={{ color: "#999" }}>{(m.size_bytes/1024).toFixed(1)} KB</span>
                    </label>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button
                  disabled={selectedMaterialIds.length === 0 || isUploading}
                  className="knowledge-graph-button"
                  onClick={async () => {
                    setIsLoading(true);
                    try {
                      const draft = await extractDraft(selectedMaterialIds, {
                        assessmentId: selectedAssessment,
                        autoTag: Boolean(selectedAssessment),
                        graphName,
                      });
                      setDraftBatchId(draft.batch_id || null);
                      setCurrentGraphId(draft.batch_id || null);
                      setAllNodes(draft.nodes || []);
                      setAllEdges(draft.edges || []);
                      setNodes(draft.nodes || []);
                      setEdges(draft.edges || []);
                      setBaseNodes(draft.nodes || []);
                      setBaseEdges(draft.edges || []);
                      reactFlowInstance?.fitView({ padding: 0.1 });
                      let message = `✅ Draft created from ${selectedMaterialIds.length} saved files.`;
                      if (draft.tagging_started) {
                        message += `\nTagging questions for ${selectedAssessment || "assessment"}…`;
                      }
                      alert(message);
                      setGraphName("");
                      // refresh lists
                      try { const res = await listGraphs(); setGraphs(res.graphs || []);} catch {}
                      refreshProgressInsights(selectedAssessment);
                    } catch (e) {
                      alert(`❌ Extract failed: ${e.message}`);
                    } finally {
                      setIsLoading(false);
                    }
                  }}
                >
                  Extract Draft From Selected
                </button>
                <button
                  className="knowledge-graph-button knowledge-graph-button-outline"
                  onClick={async () => {
                    try {
                      const res = await listMaterials();
                      setMaterials(res.materials || []);
                    } catch (e) {
                      alert(`Failed to refresh materials: ${e.message}`);
                    }
                  }}
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
          </>
          )}
          {false && (
            <>
            {/* Enhanced Multi-File Upload for SQL Lectures */}
            <div className="knowledge-graph-card">
              <div className="knowledge-graph-card-header">
                <h2>📚 Upload SQL Lecture Materials</h2>
              </div>
              <div className="knowledge-graph-card-content">
                <input
                  type="text"
                  className="knowledge-graph-input"
                  placeholder="Graph name (optional)"
                  value={graphName}
                  onChange={(e) => setGraphName(e.target.value)}
                  style={{ marginBottom: "10px" }}
                />
                <input
                  type="file"
                  accept=".pdf,.txt,.md,.docx"
                  multiple
                  onChange={handleMultiFileUpload}
                  className="knowledge-graph-input"
                  disabled={isUploading}
                  style={{ marginBottom: "10px" }}
                />
                <div
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginBottom: "10px",
                  }}
                >
                  Select multiple PDF, TXT, MD, or DOCX files containing SQL
                  lecture materials
                </div>

                {isUploading && (
                  <div
                    style={{
                      padding: "10px",
                      background: "#f8f9fa",
                      border: "1px solid #e9ecef",
                      borderRadius: "4px",
                      marginTop: "10px",
                    }}
                  >
                    <div style={{ color: "#0066cc", marginBottom: "8px" }}>
                      🔄 Processing {uploadFiles.length} files...
                    </div>
                    {uploadFiles.length > 0 && (
                      <div style={{ maxHeight: "120px", overflowY: "auto" }}>
                        {uploadFiles.map((file, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              padding: "2px 0",
                              fontSize: "12px",
                              color: "#555",
                            }}
                          >
                            📄 {file.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            </>
          )}

            {/* Actions */}
            <div className="knowledge-graph-actions">
              <button
                onClick={handleSaveGraph}
                className="knowledge-graph-button knowledge-graph-button-outline"
              >
                💾 Save Graph
              </button>
              <button
                onClick={exportGraph}
                className="knowledge-graph-button knowledge-graph-button-outline"
              >
                Export
              </button>
              <button onClick={clearGraph} className="knowledge-graph-button">
                Clear
              </button>
            </div>
          </div>
        )}

        {/* CENTER + RIGHT */}
        <div className="knowledge-graph-flow-and-details">
          {/* Graph */}
          <div
            className="knowledge-graph-flow-container"
            ref={reactFlowWrapper}
          >
            {isLoading ? (
              <div className="knowledge-graph-loading">
                <div className="knowledge-graph-spinner" />
              </div>
            ) : (
              <ReactFlow
                style={{ width: "100%", height: "100%" }}
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onNodeDrag={onNodeDrag}
                onNodeDragStart={onNodeDragStart}
                onNodeDragStop={onNodeDragStop}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onInit={setReactFlowInstance}
                onNodeClick={onNodeClick}
                onPaneClick={() => setSelectedNode(null)}
                onSelectionChange={(sel) => {
                  const selected = sel?.nodes || [];
                  if (!selected.length) {
                    setSelectedNode(null);
                    if (graphSelectedAnchors.length) {
                      setGraphSelectedAnchors([]);
                    }
                    return;
                  }

                  const seen = new Set();
                  const anchors = [];
                  selected.forEach((node) => {
                    const type = node?.data?.nodeType;
                    const labels = node?.data?.labels || node?.labels;
                    const props = node?.data?.properties || {};
                    const isConceptNode =
                    (typeof type === "string" &&
                      ["concept", "syllabus topics"].includes(type.toLowerCase())) ||
                    (Array.isArray(labels) &&
                      labels.some(
                        (lbl) =>
                          typeof lbl === "string" &&
                          ["concept", "syllabus topics"].includes(lbl.toLowerCase())
                      )) ||
                    props.is_syllabus ||
                    props.graph_type === "syllabus";

                  if (!isConceptNode) {
                    return;
                  }

                    const label =
                      (node?.data?.label ||
                        props.canonical_label ||
                        props.name ||
                        props.label ||
                        node?.id ||
                        "")
                        .trim();
                    if (!label) {
                      return;
                    }
                    const key = label.toLowerCase();
                    if (seen.has(key)) {
                      return;
                    }
                    seen.add(key);
                    anchors.push(label);
                  });

                  let changed = anchors.length !== graphSelectedAnchors.length;
                  if (!changed) {
                    for (let i = 0; i < anchors.length; i += 1) {
                      if (anchors[i] !== graphSelectedAnchors[i]) {
                        changed = true;
                        break;
                      }
                    }
                  }
                  if (changed) {
                    setGraphSelectedAnchors(anchors);
                  }
                }}
                onNodeDoubleClick={onNodeDoubleClick}
                onNodeContextMenu={onNodeContextMenu}
                onEdgeClick={onEdgeClick}
                fitView
                defaultEdgeOptions={{
                  style: { strokeWidth: 2 },
                  markerEnd: { type: MarkerType.ArrowClosed },
                }}
              >
                <Controls />
                <MiniMap
                  nodeColor={(node) =>
                    node.data?.properties?.errorTypes?.length ? "red" : "green"
                  }
                  nodeStrokeWidth={3}
                  zoomable
                  pannable
                />
                <Background variant="dots" gap={12} size={1} />
                <Panel position="top-left">
                  <strong>
                    {showStudents ? "Student Journey View" : "Concept View"}
                  </strong>
                </Panel>
                <Panel position="top-right" className="controls-panel">
                  <p>
                    {nodes.length} nodes, {edges.length} connections
                  </p>
                  {showStudents && (
                    <p style={{ fontSize: "11px", margin: "5px 0 0 0" }}>
                      Each student has individual assessment/question paths
                    </p>
                  )}
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "8px" }}>
                    <input
                      type="text"
                      value={graphName}
                      onChange={(e) => setGraphName(e.target.value)}
                      placeholder="Snapshot name (optional)"
                      style={{
                        padding: "4px 6px",
                        fontSize: "12px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleSaveSnapshot}
                      disabled={isSnapshotSaving}
                      style={{ fontSize: "12px" }}
                    >
                      {isSnapshotSaving ? "Saving…" : "Save Snapshot"}
                    </button>
                  </div>
                  {snapshotMessage && (
                    <p style={{ fontSize: "11px", marginTop: "4px" }}>
                      {snapshotMessage}
                    </p>
                  )}
                </Panel>
              </ReactFlow>
            )}
          </div>

          {/* RIGHT PANEL: stack details + student overview */}
          {rightPanelVisible && (
            <div className="knowledge-graph-right-panel">
              {/* Details panel */}
              <div className="knowledge-graph-node-details">
                {selectedNode ? (
                  <>
                    <h3>Node Details</h3>
                    <p>
                      <strong>Type:</strong>{" "}
                      {selectedNode.data.nodeType || "Unknown"}
                    </p>
                    <p>
                      <strong>Label:</strong> {selectedNode.data.label}
                    </p>

                  {/* Tagged Concepts for Question and StudentQuestion Nodes */}
                  {['Question', 'StudentQuestion'].includes(selectedNode.data.nodeType) && (
                    <div style={{
                      margin: '16px 0',
                      padding: '10px',
                      background: '#f8f9fa',
                      borderRadius: '5px',
                      border: '1px solid #e9ecef',
                    }}>
                      <h4>🏷️ Tagged Concepts</h4>
                      {Array.isArray(selectedNode.data.properties?.tagged_concepts) &&
                      selectedNode.data.properties.tagged_concepts.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {selectedNode.data.properties.tagged_concepts.map((concept, idx) => (
                            <span
                              key={idx}
                              style={{
                                background: '#e0e7ff',
                                color: '#1e40af',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                fontSize: '13px',
                                fontWeight: 500,
                                border: '1px solid #b6c3e6',
                              }}
                            >
                              {concept}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: '#888', fontSize: '13px' }}>
                          No tagged concepts.
                        </div>
                      )}
                    </div>
                  )}

                  {['Question', 'StudentQuestion'].includes(selectedNode.data.nodeType) && (
                    <div
                      style={{
                        margin: '16px 0',
                        padding: '10px',
                        background: '#fff7ed',
                        borderRadius: '5px',
                        border: '1px solid #fed7aa',
                      }}
                    >
                      <h4>🧭 Canonical Concepts</h4>
                      {Array.isArray(selectedNode.data.properties?.canonical_concept_mappings) &&
                      selectedNode.data.properties.canonical_concept_mappings.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {selectedNode.data.properties.canonical_concept_mappings.map((mapping, idx) => (
                            <div
                              key={idx}
                              style={{
                                border: '1px solid #fec89a',
                                borderRadius: '6px',
                                padding: '8px',
                                background: '#fffaf3',
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontWeight: 600 }}>{mapping.canonical_label || mapping.raw}</span>
                                <span
                                  style={{
                                    background: mapping.needs_review ? '#fee2e2' : '#dcfce7',
                                    color: mapping.needs_review ? '#991b1b' : '#166534',
                                    borderRadius: 12,
                                    padding: '2px 8px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                  }}
                                >
                                  {mapping.needs_review ? 'needs review' : mapping.status || 'verified'}
                                </span>
                              </div>
                              <div style={{ fontSize: '12px', color: '#5f370e', display: 'grid', gap: '4px' }}>
                                <span>raw: {mapping.raw}</span>
                                {mapping.wikidata_qid && (
                                  <a
                                    href={`https://www.wikidata.org/wiki/${mapping.wikidata_qid}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: '#1d4ed8', textDecoration: 'underline' }}
                                  >
                                    Wikidata: {mapping.wikidata_qid}
                                  </a>
                                )}
                                {mapping.source && <span>source: {mapping.source}</span>}
                                {Array.isArray(mapping.metadata?.aliases) && mapping.metadata.aliases.length > 0 && (
                                  <span>aliases: {mapping.metadata.aliases.join(', ')}</span>
                                )}
                                {Array.isArray(mapping.metadata?.original_labels) && mapping.metadata.original_labels.length > 0 && (
                                  <span>original labels: {mapping.metadata.original_labels.join(', ')}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: '#92400e', fontSize: '13px' }}>
                          No canonical mappings recorded for this question yet.
                        </div>
                      )}
                      {Array.isArray(selectedNode.data.properties?.needs_review_concepts) &&
                        selectedNode.data.properties.needs_review_concepts.length > 0 && (
                          <div style={{ marginTop: '8px', fontSize: '12px', color: '#9a3412' }}>
                            Needs review: {selectedNode.data.properties.needs_review_concepts.join(', ')}
                          </div>
                        )}
                    </div>
                  )}

                  {selectedNode.data.nodeType === 'Concept' && (
                    <div
                      style={{
                        margin: '16px 0',
                        padding: '10px',
                        background: '#eef2ff',
                        borderRadius: '5px',
                        border: '1px solid #c7d2fe',
                      }}
                    >
                      <h4>Canonical Metadata</h4>
                      <div style={{ fontSize: '12px', color: '#312e81', display: 'grid', gap: '4px' }}>
                        {selectedNode.data.properties?.canonical_label && (
                          <span>label: {selectedNode.data.properties.canonical_label}</span>
                        )}
                        {selectedNode.data.properties?.canonical_status && (
                          <span>status: {selectedNode.data.properties.canonical_status}</span>
                        )}
                        {selectedNode.data.properties?.canonical_wikidata_qid && (
                          <a
                            href={`https://www.wikidata.org/wiki/${selectedNode.data.properties.canonical_wikidata_qid}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: '#1d4ed8', textDecoration: 'underline' }}
                          >
                            Wikidata: {selectedNode.data.properties.canonical_wikidata_qid}
                          </a>
                        )}
                        {(Array.isArray(canonicalMetadata?.aliases) && canonicalMetadata.aliases.length > 0
                          ? canonicalMetadata.aliases
                          : Array.isArray(selectedNode.data.properties?.canonical_aliases)
                            ? selectedNode.data.properties.canonical_aliases
                            : null) && (
                          <span>
                            aliases:{' '}
                            {(canonicalMetadata?.aliases || selectedNode.data.properties?.canonical_aliases).join(', ')}
                          </span>
                        )}
                        {Array.isArray(canonicalMetadata?.original_labels) && canonicalMetadata.original_labels.length > 0 && (
                          <span>
                            original labels: {canonicalMetadata.original_labels.join(', ')}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                    {/* Show specific details based on node type */}
                    {selectedNode.data.nodeType === "Student" && (
                      <div
                        style={{
                          backgroundColor: "#f0f8ff",
                          padding: "10px",
                          borderRadius: "5px",
                          margin: "10px 0",
                          border: "1px solid #b8daf0",
                        }}
                      >
                        <h4>👨‍🎓 Student Information</h4>
                        <p>
                          <strong>NetID:</strong>{" "}
                          {selectedNode.data.properties?.netid}
                        </p>
                        <p>
                          <em>
                            Click on this student's assessment or question nodes
                            to see detailed performance data.
                          </em>
                        </p>
                      </div>
                    )}

                    {selectedNode.data.nodeType === "StudentAssessment" && (
                      <div
                        style={{
                          backgroundColor: "#f0fff0",
                          padding: "10px",
                          borderRadius: "5px",
                          margin: "10px 0",
                          border: "1px solid #90ee90",
                        }}
                      >
                        <h4>📝 Assessment Attempt</h4>
                        <p>
                          <strong>Student:</strong>{" "}
                          {selectedNode.data.properties?.student_netid}
                        </p>
                        <p>
                          <strong>Assessment ID:</strong>{" "}
                          {selectedNode.data.properties?.assessment_id}
                        </p>
                        <p>
                          <em>
                            This represents{" "}
                            {selectedNode.data.properties?.student_netid}'s
                            attempt at{" "}
                            {selectedNode.data.properties?.assessment_id}
                          </em>
                        </p>
                      </div>
                    )}

                    {selectedNode.data.nodeType === "StudentQuestion" && (
                      <div
                        style={{
                          backgroundColor: "#f0f8ff",
                          padding: "10px",
                          borderRadius: "5px",
                          margin: "10px 0",
                          border: "1px solid #87ceeb",
                        }}
                      >
                        <h4>❓ Question Attempt</h4>
                        <p>
                          <strong>Student:</strong>{" "}
                          {selectedNode.data.properties?.student_netid}
                        </p>
                        <p>
                          <strong>Question ID:</strong>{" "}
                          {selectedNode.data.properties?.question_id}
                        </p>
                        <p>
                          <strong>Assessment:</strong>{" "}
                          {selectedNode.data.properties?.assessment_id}
                        </p>
                        <p>
                          <em>
                            This represents{" "}
                            {selectedNode.data.properties?.student_netid}'s work
                            on this specific question
                          </em>
                        </p>
                      </div>
                    )}

                    {selectedNode.data.nodeType === "StudentError" && (
                      <div
                        style={{
                          backgroundColor: "#fff8f0",
                          padding: "10px",
                          borderRadius: "5px",
                          margin: "10px 0",
                          border: "1px solid #ffb366",
                        }}
                      >
                        <h4>❌ Error Encountered</h4>
                        <p>
                          <strong>Student:</strong>{" "}
                          {selectedNode.data.properties?.student_netid}
                        </p>
                        <p>
                          <strong>Error Type:</strong>{" "}
                          {selectedNode.data.properties?.error_type}
                        </p>
                        <p>
                          <strong>Question:</strong>{" "}
                          {selectedNode.data.properties?.question_id}
                        </p>
                        <p>
                          <em>
                            This error was encountered by{" "}
                            {selectedNode.data.properties?.student_netid} on
                            question {selectedNode.data.properties?.question_id}
                          </em>
                        </p>
                      </div>
                    )}

                    {/* Show progress information if available */}
                    {selectedNode.data.progress && (
                      <div
                        style={{
                          backgroundColor: "#f8f9fa",
                          padding: "10px",
                          borderRadius: "5px",
                          margin: "10px 0",
                          border: "1px solid #e9ecef",
                        }}
                      >
                        <h4>📊 Student Progress</h4>
                        <p>
                          <strong>Completion Rate:</strong>{" "}
                          {selectedNode.data.progress.percentage}%
                        </p>
                        <p>
                          <strong>Students:</strong>{" "}
                          {selectedNode.data.progress.completed} /{" "}
                          {selectedNode.data.progress.total_possible}{" "}
                          completions
                        </p>
                        <p>
                          <strong>Questions:</strong>{" "}
                          {selectedNode.data.progress.questions_count} questions
                          use this concept
                        </p>

                        {selectedNode.data.progress.questions && (
                          <details style={{ marginTop: "10px" }}>
                            <summary
                              style={{ cursor: "pointer", fontWeight: "bold" }}
                            >
                              Question Breakdown
                            </summary>
                            <div style={{ marginTop: "5px", fontSize: "12px" }}>
                              {selectedNode.data.progress.questions.map(
                                (q, idx) => (
                                  <div key={idx} style={{ margin: "2px 0" }}>
                                    <strong>{q.question_id}:</strong>{" "}
                                    {q.completed}/{q.total} students
                                  </div>
                                )
                              )}
                            </div>
                          </details>
                        )}
                      </div>
                    )}

                    <h4>All Properties</h4>
                    <div
                      style={{
                        maxHeight: "200px",
                        overflowY: "auto",
                        backgroundColor: "#f8f9fa",
                        padding: "10px",
                        borderRadius: "5px",
                        fontSize: "12px",
                      }}
                    >
                      <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {Object.entries(selectedNode.data.properties || {}).map(
                          ([k, v]) => (
                            <li key={k} style={{ marginBottom: "5px" }}>
                              <strong>{k}:</strong> {String(v)}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  </>
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      color: "#666",
                    }}
                  >
                    <p>Click a node to see its details here.</p>
                    {/* <div style={{ fontSize: "12px", marginTop: "10px" }}>
                    <p>
                      <strong>Node Types:</strong>
                    </p>
                    <p>🎓 Students (red) - Individual learners</p>
                    <p>
                      📝 Assessments (green) - Student's assessment attempts
                    </p>
                    <p>❓ Questions (blue) - Student's question attempts</p>
                    <p>❌ Errors (orange) - Errors encountered by students</p>
                  </div> */}
                  </div>
                )}
              </div>

              {ENABLE_STUDENT_PERFORMANCE && (
                <div className="knowledge-graph-student-performance-overview">
                  <h3>Student Performance Overview</h3>
                  <h4>Student Profile</h4>
                  {profile && profile.length > 0 ? (
                    profile.map((assessment) => (
                      <div
                        key={assessment.assessment_id}
                        style={{ marginBottom: 16 }}
                      >
                        <strong>Assessment:</strong> {assessment.assessment_id}
                        {assessment.questions.map((q) => (
                          <div
                            key={q.question_id}
                            style={{ marginLeft: 16, marginBottom: 8 }}
                          >
                            <div>
                              <strong>Question:</strong> {q.question_id}
                            </div>
                            {"submission_count" in q && (
                              <div>
                                <strong>Submissions:</strong> {q.submission_count}
                              </div>
                            )}
                            <div>
                              <strong>Errors:</strong>
                              <ul>
                                {Array.from(
                                  new Set(q.errors.filter((e) => e))
                                ).map((err, idx) => (
                                  <li key={idx}>{err}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ))}
                      </div>
                    ))
                  ) : (
                    <div>No performance data.</div>
                  )}

                  {/* Assessment Advice */}
                  {assessmentErrors && typeof assessmentErrors === "string" && (
                    <div
                      className="knowledge-graph-assessment-errors"
                      style={{ marginTop: 24 }}
                    >
                      <h4>Assessment Advice</h4>
                      <ReactMarkdown>{assessmentErrors}</ReactMarkdown>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {edgeToEdit && (
        <div className="edge-edit-overlay">
          <div className="edge-edit-box">
            <label style={{ display: "block", marginBottom: 8 }}>
              Relationship:
              <input
                style={{ width: "100%", marginTop: 4, padding: "4px" }}
                value={edgeLabelDraft}
                onChange={(e) => setEdgeLabelDraft(e.target.value)}
              />
            </label>
            <div className="edge-edit-buttons">
              <button
                onClick={() => {
                  setEdges((es) => es.filter((e) => e.id !== edgeToEdit.id));
                  setEdgeToEdit(null);
                }}
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setEdges((es) =>
                    es.map((e) =>
                      e.id === edgeToEdit.id
                        ? { ...e, label: edgeLabelDraft }
                        : e
                    )
                  );
                  setEdgeToEdit(null);
                }}
              >
                Save
              </button>
              <button onClick={() => setEdgeToEdit(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showNodeEdit && (
        <NodeEditModal
          node={selectedNode || null}
          onClose={() => setShowNodeEdit(false)}
          onUpdate={(id, payload) => { updateNodeInline(id, payload); setShowNodeEdit(false); }}
          onDelete={(id) => { deleteNodeInline(id); setShowNodeEdit(false); }}
          onCreate={(payload) => { 
            const label = (payload?.label || "").trim();
            if (!label) return;
            const nn = {
              id: `n-${Date.now()}`,
              data: {
                label,
                properties: payload?.properties || { errorTypes: [], comments: "" },
              },
              position: { x: 200, y: 200 },
            };
            setNodes((ns) => [...ns, nn]);
            setShowNodeEdit(false);
          }}
        />
      )}

      {showUploadModal && (
  <div className="modal-overlay">
    <div
      className="modal-content"
      style={{
        maxWidth: "1100px",
        width: "90vw",
        maxHeight: "90vh",
        overflowY: "auto",
      }}
    >
      <h3>Upload Material</h3>
      {canonicalLoading && (
        <div style={{ fontSize: "11px", color: "#2563eb", marginBottom: 8 }}>
          Loading canonical metadata…
        </div>
      )}
      {canonicalError && (
        <div style={{ fontSize: "11px", color: "#b91c1c", marginBottom: 8 }}>
          {canonicalError}
        </div>
      )}

      {syllabusDraft ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ marginBottom: 0 }}>
            Review the extracted syllabus concepts. Edit as needed before committing.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontWeight: 600 }}>Graph name (optional)</label>
            <input
              className="knowledge-graph-input"
              value={graphName}
              onChange={(e) => setGraphName(e.target.value)}
              placeholder="e.g., CS411 Syllabus 2024"
            />
          </div>
          <div
            style={{
              maxHeight: 360,
              overflowY: "auto",
              paddingRight: 4,
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            {syllabusDraftConcepts.map((label, idx) => {
              const { canonical, theme } = resolveLabelTheme(label);
              return (
                <div
                  key={idx}
                  style={{
                    flex: "0 1 240px",
                    minWidth: 220,
                    border: `1px solid ${theme.border}`,
                    background: theme.background,
                    borderRadius: 9999,
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                    <input
                      className="knowledge-graph-input"
                      style={{
                        width: "100%",
                        borderRadius: 9999,
                        padding: "6px 10px",
                        fontSize: 13,
                      }}
                      value={label}
                      onChange={(e) => {
                        const next = [...syllabusDraftConcepts];
                        next[idx] = e.target.value;
                        setSyllabusDraftConcepts(next);
                      }}
                    />
                    <span style={{ color: theme.text, fontSize: "11px", fontWeight: 600 }}>
                      {canonical
                        ? `${theme.label} • ${canonical.status || "unknown status"}`
                        : `${theme.label} • not yet canonicalized`}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      setSyllabusDraftConcepts((prev) => prev.filter((_, i) => i !== idx));
                    }}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: theme.text,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontSize: 14,
                      padding: 0,
                      lineHeight: 1,
                    }}
                    aria-label={`Remove ${label}`}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
          <button
            className="knowledge-graph-button knowledge-graph-button-outline"
            onClick={() => setSyllabusDraftConcepts((prev) => [...prev, ""])}
          >
            + Add Concept
          </button>
          {draftError && (
            <div style={{ color: "#b91c1c" }}>{draftError}</div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              className="knowledge-graph-button knowledge-graph-button-outline"
              onClick={() => {
                resetUploadState();
                setShowUploadModal(false);
              }}
            >
              Cancel
            </button>
            <button
              className="knowledge-graph-button"
              disabled={isDraftSubmitting}
              onClick={handleCommitSyllabusDraft}
            >
              {isDraftSubmitting ? "Committing…" : "Commit & Ingest"}
            </button>
            <button onClick={handleCreateSyllabusDraft}>Create Syllabus Draft</button>

            {/* render + let user edit syllabusDraftConcepts (array of strings) in your UI */}

            {/* After user reviews/edits: */}
            <button onClick={handleCommitSyllabus} disabled={!draftId}>Commit Syllabus</button>

          </div>
        </div>
      ) : conceptDraft ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <p style={{ marginBottom: 0 }}>
            Review the extracted concepts. Edit or curate the list, then commit to create a concept subgraph.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontWeight: 600 }}>Graph name (optional)</label>
            <input
              className="knowledge-graph-input"
              value={graphName}
              onChange={(e) => setGraphName(e.target.value)}
              placeholder="e.g., Lecture 1 - Indexing"
            />
          </div>
          <div
            style={{
              maxHeight: 360,
              overflowY: "auto",
              paddingRight: 4,
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            {conceptDraftConcepts.length ? (
              conceptDraftConcepts.map((label, idx) => {
                const { canonical, theme } = resolveLabelTheme(label);
                return (
                  <div
                    key={`concept-draft-${idx}`}
                    style={{
                      flex: "0 1 240px",
                      minWidth: 220,
                      border: `1px solid ${theme.border}`,
                      borderRadius: 9999,
                      padding: "6px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      background: theme.background,
                    }}
                  >
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
                      <input
                        className="knowledge-graph-input"
                        style={{
                          width: "100%",
                          borderRadius: 9999,
                          padding: "6px 10px",
                          fontSize: 13,
                        }}
                        value={label}
                        onChange={(e) => {
                          const next = [...conceptDraftConcepts];
                          next[idx] = e.target.value;
                          setConceptDraftConcepts(next);
                        }}
                      />
                      <span style={{ color: theme.text, fontSize: "11px", fontWeight: 600 }}>
                        {canonical
                          ? `${theme.label} • ${canonical.status || "unknown status"}`
                          : `${theme.label} • not yet canonicalized`}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setConceptDraftConcepts((prev) => prev.filter((_, i) => i !== idx));
                      }}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: theme.text,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontSize: 14,
                        padding: 0,
                        lineHeight: 1,
                      }}
                      aria-label={`Remove ${label}`}
                    >
                      ×
                    </button>
                  </div>
                );
              })
            ) : (
              <div style={{ fontSize: "13px", color: "#6b7280" }}>
                No concepts were detected. Add entries below and commit when ready.
              </div>
            )}
          </div>
          <button
            className="knowledge-graph-button knowledge-graph-button-outline"
            onClick={() =>
              setConceptDraftConcepts((prev) => [...prev, ""])
            }
          >
            + Add Concept
          </button>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontWeight: 600 }}>Attach to existing concepts (optional)</label>
            {conceptAnchorChoices.length ? (
              <>
                <select
                  multiple
                  className="knowledge-graph-input"
                  value={selectedConceptAnchors}
                  onChange={(e) => {
                    const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                    setSelectedConceptAnchors(values);
                  }}
                  size={Math.min(6, Math.max(conceptAnchorChoices.length, 3))}
                >
                  {conceptAnchorChoices.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: "12px", color: "#6b7280" }}>
                  Hold Cmd/Ctrl while clicking to select multiple anchors.
                </span>
              </>
            ) : (
              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                Load the main graph to choose anchors for this concept batch.
              </span>
            )}
          </div>
          {draftError && (
            <div style={{ color: "#b91c1c" }}>{draftError}</div>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              className="knowledge-graph-button knowledge-graph-button-outline"
              onClick={() => {
                resetUploadState();
                setShowUploadModal(false);
              }}
            >
              Cancel
            </button>
            <button
              className="knowledge-graph-button"
              disabled={isConceptSubmitting}
              onClick={handleCommitConceptDraft}
            >
              {isConceptSubmitting ? "Committing..." : "Commit & Create Subgraph"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <label style={{ display: "block", marginBottom: "6px" }}>
            <strong>What are you uploading?</strong>
          </label>

          <div style={{ marginBottom: "10px" }}>
            <label>
              <input
                type="checkbox"
                value="syllabus"
                checked={selectedTypes.includes("syllabus")}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedTypes((prev) =>
                    e.target.checked ? [...prev, val] : prev.filter((x) => x !== val)
                  );
                }}
              />
              Syllabus
            </label>
            <br />
            <label>
              <input
                type="checkbox"
                value="concepts"
                checked={selectedTypes.includes("concepts")}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedTypes((prev) =>
                    e.target.checked ? [...prev, val] : prev.filter((x) => x !== val)
                  );
                }}
              />
              Concepts
            </label>
          </div>

          <input
            type="file"
            accept=".pdf,.txt,.md,.docx"
            multiple
            onChange={(e) => setUploadFiles(Array.from(e.target.files))}
            className="knowledge-graph-input"
            style={{ marginBottom: "10px" }}
          />

          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
            <label style={{ fontWeight: 600 }}>Graph name (optional)</label>
            <input
              className="knowledge-graph-input"
              value={graphName}
              onChange={(e) => setGraphName(e.target.value)}
              placeholder="Name this draft for easy reference"
            />
          </div>

          {selectedTypes.includes("concepts") && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
              <label style={{ fontWeight: 600 }}>What is this material about? (optional)</label>
              <textarea
                className="knowledge-graph-input"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="e.g., SQL basics for undergrad DB course"
                rows={3}
              />
            </div>
          )}

          {selectedTypes.includes("concepts") && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
              <label style={{ fontWeight: 600 }}>Attach to existing concepts (optional)</label>
              {conceptAnchorChoices.length ? (
                <>
                  <select
                    multiple
                    className="knowledge-graph-input"
                    value={selectedConceptAnchors}
                    onChange={(e) => {
                      const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                      setSelectedConceptAnchors(values);
                    }}
                    size={Math.min(6, Math.max(conceptAnchorChoices.length, 3))}
                  >
                    {conceptAnchorChoices.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: "12px", color: "#6b7280" }}>
                    Hold Cmd/Ctrl while clicking to select multiple anchors.
                  </span>
                </>
              ) : (
                <span style={{ fontSize: "12px", color: "#6b7280" }}>
                  Load the main graph to choose anchors for this concept batch.
                </span>
              )}
            </div>
          )}

          {draftError && (
            <div style={{ color: "#b91c1c", marginBottom: 8 }}>{draftError}</div>
          )}

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              className="knowledge-graph-button"
              disabled={isUploading}
              onClick={async () => {
                if (!uploadFiles.length) {
                  alert("Please choose a file");
                  return;
                }
                if (!selectedTypes.length) {
                  alert("Please choose a type");
                  return;
                }
                if (selectedTypes.includes("syllabus") && selectedTypes.length > 1) {
                  alert("Please handle syllabus and other uploads separately.");
                  return;
                }
                if (selectedTypes.includes("concepts") && selectedTypes.length > 1) {
                  alert("Please handle concept uploads separately.");
                  return;
                }

                if (selectedTypes.includes("syllabus")) {
                  await handleStartSyllabusDraft();
                  return;
                }

                if (selectedTypes.includes("concepts")) {
                  await handleStartConceptDraft();
                  return;
                }

                setIsUploading(true);
                try {
                  const formData = new FormData();
                  uploadFiles.forEach((file) => formData.append("files", file));
                  selectedTypes.forEach((t) => formData.append("types", t));
                  formData.append("mode", selectedTypes.includes("syllabus") ? "syllabus" : "concepts");
                  const response = await fetchWithAuth(
                    `${API_URL}/api/knowledge-graph/from-multiple-files`,
                    { method: "POST", body: formData }
                  );

                  if (!response.ok) throw new Error("Upload failed");
                  const graph = await response.json();

                  setNodes(graph.nodes || []);
                  setEdges(graph.edges || []);
                  resetUploadState();
                  setShowUploadModal(false);
                } catch (err) {
                  alert(`❌ ${err.message}`);
                } finally {
                  setIsUploading(false);
                }
              }}
            >
              {isUploading ? "Uploading…" : "Upload"}
            </button>
            <button
              className="knowledge-graph-button knowledge-graph-button-outline"
              onClick={() => {
                resetUploadState();
                setShowUploadModal(false);
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)}

    </div>
  );
}

export default function KnowledgeGraphPage() {
  return (
    <ReactFlowProvider>
      <KnowledgeGraph />
    </ReactFlowProvider>
  );
}
